import { useEffect, useRef } from "react";
import Konva from "konva";
import vtkCoordinate from "vtk.js/Sources/Rendering/Core/Coordinate";
import vtkInteractorStyleManipulator from 'vtk.js/Sources/Interaction/Style/InteractorStyleManipulator';
import { constants, logDebug, isNullOrUndefined, debugCallback, getDUName } from "../js/Common";
import { Vector3 } from "../js/Vector3";
import {
    CenterlineBranchSelection,
    CenterlineDistanceBranchSelection,
    CenterlineTransitionBranchSelection,
    CenterlineType
} from "../CenterlineMeasurement/CenterlinePlaneShared";
import * as vtkMath from 'vtk.js/Sources/Common/Core/Math';
import { createBasePipeline } from "../CenterlineMeasurement/BranchedPoints/CenterlineMeasurementPipeline";
import { setRendererViewPlaneNormal } from "../Camera/AdjustCamera";
const isDebugging = false;

function log(msg) {
    logDebug("Visualization.StentPlanning", isDebugging, msg);
}

function debug(callback) {
    debugCallback(isDebugging, callback);
}

/**
 * @typedef {Object} UpdateRingArgs
 * @property {Group} group - The Konva group the label belongs to, if any.
 * @property {string} measurementName - The name of measurement the label is tied to. Needed to pass along to callback functions.
 * @property {Array<Object>} unifiedBranchInfo - The unified branch info for the measurement.
 * @property {import('../StentPlanningCenterlines/StentPlanningCenterlines.js'.Branch)} branchType - The branch type the measurement is tied to.
 * @property {boolean} isIndexValid - Flag to specify if the index for the measurement is valid or not.
 * @property {Meshes} meshes - The meshes object containing the diameter rings and centerline data.
 * @property {object} labelInfo - The label info object containing label properties.
 * @property {vtkRenderWindowInteractor} parentContext - The parent VTK rendering context.
 * @property {Konva.Line} labelLine - The Konva line connecting the label to the diameter ring.
 */

/**
 * @typedef {Object} StentPlanningDiameters
 * @property {float} min
 * @property {float} max
 * @property {float} average
 */

/**
 * @typedef {Object} Measurement
 * @property {float} min
 * @property {float} max
 * @property {float} average
 * @property {StentPlanningDiameters} stentPlanningDiameters
 */


const leftSideXOffset = 0.05;
const rightSideXOffset = 0.75;

const indentation = 0.0;

const labelEnabledIcon = '\uf06e'; //fontawesome 'eye'
const labelDisabledIcon = '\uf070'; //fontawesome 'eye-slash'

const labelExpandedIcon = '\uf078'; //fontawesome 'chevron-down'
const labelCollapsedIcon = '\uf054'; //fontawesome 'chevron-right'

const labelFontSize = 16;
const labelPadding = 5;
const labelIconFontFamily = 'Font Awesome 6 Free';
const labelTextFontFamily = 'Open Sans';
const labelIconFontStyle = 800;

const DisplayMeasurementType = Object.freeze({
    MIN: "MIN",
    MAX: "MAX",
    AVERAGE: "AVERAGE",
    LUMEN: "LUMEN"
});

const LabelType = Object.freeze({
    DIAMETER: "DIAMETER",
    OFFSET_DIAMETER: "OFFSET_DIAMETER",
    MIDPOINT_DIAMETER: "MIDPOINT_DIAMETER",
    LENGTH: "LENGTH",
    ANGLE: "ANGLE",
    CARM: "C-ARM"
});

const MeasurementBounds = Object.freeze({
    TRANSITION_TRUNK: "TransitionTrunk",
    TRANSITION_LEFT: "TransitionLeft",
    TRANSITION_RIGHT: "TransitionRight",
    DISTANCE_TRUNK: "DistanceTrunk",
    DISTANCE_TRUNK_CUSP: "DistanceTrunkCusp",
    DISTANCE_LEFT: "DistanceLeft",
    DISTANCE_RIGHT: "DistanceRight"
});

const measurementNames = Object.freeze({
    Renal: "Renal",
    RenalPlus5: "RenalPlus5",
    RenalPlus10: "RenalPlus10",
    RenalPlus15: "RenalPlus15",
    RenalPlus20: "RenalPlus20",
    NeckDiameter: "NeckDiameter",
    NeckLength: "NeckLength",
    Bifurc: "Bifurc",
    Right: "Right",
    RCIA: "RCIA",
    RCIAMid: "RCIAMid",
    RCIAMinus20: "RCIAMinus20",
    RCIAMinus10: "RCIAMinus10",
    RCIADist: "RCIADist",
    RCIALength: "RCIALength",
    REIA: "REIA",
    REIAMid: "REIAMid",
    REIAMin: "REIAMin",
    REIADist: "REIADist",
    BoaLength: "BoaLength",
    BoaDiameter: "BoaDiameter",
    BoaDiameterMax: "BoaDiameterMax",
    BoaDiameterBif: "BoaDiameterBif",
    Left: "Left",
    LCIA: "LCIA",
    LCIAMid: "LCIAMid",
    LCIAMinus20: "LCIAMinus20",
    LCIAMinus10: "LCIAMinus10",
    LCIADist: "LCIADist",
    LCIALength: "LCIALength",
    LEIA: "LEIA",
    LEIAMid: "LEIAMid",
    LEIAMin: "LEIAMin",
    LEIADist: "LEIADist",
    RenalCArm: "RenalCArm",
    LIIACArm: "LIIACArm",
    RIIACArm: "RIIACArm"
});

const renalLengths = Object.freeze([
    measurementNames.NeckLength,
    measurementNames.Bifurc,
    measurementNames.BoaLength,
    measurementNames.Right,
    measurementNames.Left,
]);

function arrayDifference(arr1, arr2) {
    return arr1.filter(item => !arr2.includes(item));
}

const measurementLabelInfo = {
    [measurementNames.Renal]: {
        labelName: "Renal",
        labelColor: "blue",
        labelType: LabelType.DIAMETER,
        xOffset: leftSideXOffset,
        yOffset: 0.05,
        landmarkIndex: 0,
        parentGroup: measurementNames.Renal,
        landmarkArrayName: constants.dataArrayNames.landmarkIds,
        firstPlaneChildren: [
            ...renalLengths,
            measurementNames.RenalPlus5,
            measurementNames.RenalPlus10,
            measurementNames.RenalPlus15,
            measurementNames.RenalPlus20
        ],
        measurementBounds: MeasurementBounds.DISTANCE_TRUNK
    },
    [measurementNames.RenalPlus5]:
    {
        labelName: "5mm  ",
        labelColor: "darkBlue",
        labelType: LabelType.OFFSET_DIAMETER,
        xOffset: leftSideXOffset + indentation,
        yOffset: 0.09,
        childYOffset: 1,
        offsetAmount: 5,
        landmarkIndex: 1,
        childGroup: measurementNames.Renal,
        landmarkArrayName: constants.dataArrayNames.landmarkIds,
        measurementBounds: MeasurementBounds.DISTANCE_TRUNK,
        offsetMeasurementName: measurementNames.Renal
    },
    [measurementNames.RenalPlus10]:
    {
        labelName: "10mm",
        labelColor: "darkBlue",
        labelType: LabelType.OFFSET_DIAMETER,
        xOffset: leftSideXOffset + indentation,
        yOffset: 0.13,
        childYOffset: 2,
        offsetAmount: 10,
        landmarkIndex: 2,
        childGroup: measurementNames.Renal,
        landmarkArrayName: constants.dataArrayNames.landmarkIds,
        measurementBounds: MeasurementBounds.DISTANCE_TRUNK,
        offsetMeasurementName: measurementNames.Renal
    },
    [measurementNames.RenalPlus15]:
    {
        labelName: "15mm",
        labelColor: "darkBlue",
        labelType: LabelType.OFFSET_DIAMETER,
        xOffset: leftSideXOffset + indentation,
        yOffset: 0.17,
        childYOffset: 3,
        offsetAmount: 15,
        landmarkIndex: 3,
        childGroup: measurementNames.Renal,
        landmarkArrayName: constants.dataArrayNames.landmarkIds,
        measurementBounds: MeasurementBounds.DISTANCE_TRUNK,
        offsetMeasurementName: measurementNames.Renal
    },
    [measurementNames.RenalPlus20]:
    {
        labelName: "20mm",
        labelColor: "darkBlue",
        labelType: LabelType.OFFSET_DIAMETER,
        xOffset: leftSideXOffset + indentation,
        yOffset: 0.21,
        childYOffset: 4,
        offsetAmount: 20,
        landmarkIndex: 4,
        childGroup: measurementNames.Renal,
        landmarkArrayName: constants.dataArrayNames.landmarkIds,
        measurementBounds: MeasurementBounds.DISTANCE_TRUNK,
        offsetMeasurementName: measurementNames.Renal
    },
    [measurementNames.NeckDiameter]: {
        labelName: "Neck",
        labelColor: "blue",
        labelType: LabelType.DIAMETER,
        xOffset: leftSideXOffset,
        yOffset: 0.26,
        landmarkIndex: 5,
        landmarkArrayName: constants.dataArrayNames.landmarkIds,
        measurementBounds: MeasurementBounds.DISTANCE_TRUNK_CUSP,
        secondPlaneChildren: [measurementNames.NeckLength],
        canBeDisabled: true
    },
    [measurementNames.NeckLength]: {
        labelName: "Neck",
        labelColor: "green",
        labelType: LabelType.LENGTH,
        xOffset: leftSideXOffset,
        yOffset: 0.31,
        landmarkIndex: 0,
        landmarkArrayName: constants.dataArrayNames.landmarkLengths,
        firstPlaneChildren: [
            measurementNames.Renal,
            ...(arrayDifference(renalLengths, [measurementNames.NeckLength])),
            measurementNames.RenalPlus5,
            measurementNames.RenalPlus10,
            measurementNames.RenalPlus15,
            measurementNames.RenalPlus20
        ],
        secondPlaneChildren: [measurementNames.NeckDiameter],
        measurementBounds: [MeasurementBounds.DISTANCE_TRUNK, MeasurementBounds.DISTANCE_TRUNK_CUSP]
    },
    [measurementNames.Bifurc]: {
        labelName: "Bifurc",
        labelColor: "green",
        labelType: LabelType.LENGTH,
        xOffset: leftSideXOffset,
        yOffset: 0.36,
        landmarkIndex: 2,
        landmarkArrayName: constants.dataArrayNames.landmarkLengths,
        firstPlaneChildren: [
            measurementNames.Renal,
            ...(arrayDifference(renalLengths, [measurementNames.Bifurc])),
            measurementNames.RenalPlus5,
            measurementNames.RenalPlus10,
            measurementNames.RenalPlus15,
            measurementNames.RenalPlus20
        ],
        secondPlaneChildren: [measurementNames.BoaDiameterBif],
        measurementBounds: [MeasurementBounds.DISTANCE_TRUNK, MeasurementBounds.DISTANCE_TRUNK_CUSP]
    },
    [measurementNames.Right]: {
        labelName: "Right",
        labelColor: "green",
        labelType: LabelType.LENGTH,
        xOffset: leftSideXOffset,
        yOffset: 0.41,
        landmarkIndex: 4,
        landmarkArrayName: constants.dataArrayNames.landmarkLengths,
        firstPlaneChildren: [
            measurementNames.Renal,
            ...(arrayDifference(renalLengths, [measurementNames.Right])),
            measurementNames.RenalPlus5,
            measurementNames.RenalPlus10,
            measurementNames.RenalPlus15,
            measurementNames.RenalPlus20
        ],
        secondPlaneChildren: [
            measurementNames.RCIADist,
            measurementNames.RCIAMinus10,
            measurementNames.RCIAMinus20,
            measurementNames.RCIAMid,
            measurementNames.RCIALength
        ],
        measurementBounds: [MeasurementBounds.DISTANCE_TRUNK, MeasurementBounds.DISTANCE_RIGHT]
    },
    [measurementNames.RCIA]: {
        labelName: "RCIA",
        labelColor: "blue",
        labelType: LabelType.DIAMETER,
        xOffset: leftSideXOffset,
        yOffset: 0.46,
        landmarkIndex: 9,
        parentGroup: measurementNames.RCIA,
        landmarkArrayName: constants.dataArrayNames.landmarkIds,
        measurementBounds: MeasurementBounds.DISTANCE_RIGHT,
        firstPlaneChildren: [measurementNames.RCIAMid, measurementNames.RCIALength]
    },
    [measurementNames.RCIAMid]:
    {
        labelName: "MID",
        labelColor: "darkBlue",
        labelType: LabelType.MIDPOINT_DIAMETER,
        xOffset: leftSideXOffset + indentation,
        yOffset: 0.50,
        childYOffset: 1,
        parentOffset: 1,
        landmarkIndex: 10,
        childGroup: measurementNames.RCIA,
        landmarkArrayName: constants.dataArrayNames.landmarkIds,
        measurementBounds: MeasurementBounds.DISTANCE_RIGHT,
        midpointBounds: [measurementNames.RCIA, measurementNames.RCIAMinus20]
    },
    [measurementNames.RCIAMinus20]:
    {
        labelName: "20mm",
        labelColor: "darkBlue",
        labelType: LabelType.OFFSET_DIAMETER,
        xOffset: leftSideXOffset + indentation,
        yOffset: 0.54,
        childYOffset: 2,
        offsetAmount: -20,
        childGroup: measurementNames.RCIA,
        landmarkIndex: 11,
        landmarkArrayName: constants.dataArrayNames.landmarkIds,
        measurementBounds: MeasurementBounds.DISTANCE_RIGHT,
        offsetMeasurementName: measurementNames.RCIADist
    },
    [measurementNames.RCIAMinus10]:
    {
        labelName: "10mm",
        labelColor: "darkBlue",
        labelType: LabelType.OFFSET_DIAMETER,
        xOffset: leftSideXOffset + indentation,
        yOffset: 0.58,
        childYOffset: 3,
        offsetAmount: -10,
        childGroup: measurementNames.RCIA,
        landmarkIndex: 12,
        landmarkArrayName: constants.dataArrayNames.landmarkIds,
        measurementBounds: MeasurementBounds.DISTANCE_RIGHT,
        offsetMeasurementName: measurementNames.RCIADist
    },
    [measurementNames.RCIADist]:
    {
        labelName: "DIST",
        labelColor: "darkBlue",
        labelType: LabelType.DIAMETER,
        xOffset: leftSideXOffset + indentation,
        yOffset: 0.62,
        childYOffset: 4,
        parentOffset: 4,
        childGroup: measurementNames.RCIA,
        landmarkIndex: 13,
        landmarkArrayName: constants.dataArrayNames.landmarkIds,
        firstPlaneChildren: [measurementNames.RCIAMinus10, measurementNames.RCIAMinus20],
        secondPlaneChildren: [measurementNames.Right, measurementNames.RCIAMid, measurementNames.RCIALength],
        measurementBounds: MeasurementBounds.DISTANCE_RIGHT
    },
    [measurementNames.RCIALength]: {
        labelName: "RCIA",
        labelColor: "green",
        labelType: LabelType.LENGTH,
        xOffset: leftSideXOffset,
        yOffset: 0.67,
        landmarkIndex: 6,
        landmarkArrayName: constants.dataArrayNames.landmarkLengths,
        firstPlaneChildren: [measurementNames.RCIA, measurementNames.RCIAMid],
        secondPlaneChildren: [measurementNames.RCIADist, measurementNames.RCIAMinus10, measurementNames.RCIAMinus20, measurementNames.RCIAMid, measurementNames.Right],
        measurementBounds: [MeasurementBounds.DISTANCE_RIGHT]
    },
    [measurementNames.REIA]: {
        labelName: "REIA",
        labelColor: "blue",
        labelType: LabelType.DIAMETER,
        xOffset: leftSideXOffset,
        yOffset: 0.72,
        parentGroup: measurementNames.REIA,
        landmarkIndex: 14,
        landmarkArrayName: constants.dataArrayNames.landmarkIds,
        measurementBounds: MeasurementBounds.DISTANCE_RIGHT,
        firstPlaneChildren: [measurementNames.REIAMid]
    },
    [measurementNames.REIAMid]:
    {
        labelName: "MID",
        labelColor: "darkBlue",
        labelType: LabelType.MIDPOINT_DIAMETER,
        xOffset: leftSideXOffset + indentation,
        yOffset: 0.76,
        childYOffset: 1,
        childGroup: measurementNames.REIA,
        landmarkIndex: 15,
        landmarkArrayName: constants.dataArrayNames.landmarkIds,
        measurementBounds: MeasurementBounds.DISTANCE_RIGHT,
        midpointBounds: [measurementNames.REIA, measurementNames.REIADist]
    },
    [measurementNames.REIAMin]:
    {
        labelName: "MIN",
        labelColor: "darkBlue",
        labelType: LabelType.DIAMETER,
        xOffset: leftSideXOffset + indentation,
        yOffset: 0.80,
        childYOffset: 2,
        parentOffset: 2,
        childGroup: measurementNames.REIA,
        landmarkIndex: 16,
        landmarkArrayName: constants.dataArrayNames.landmarkIds,
        measurementBounds: MeasurementBounds.DISTANCE_RIGHT,
        canBeDisabled: true
    },
    [measurementNames.REIADist]:
    {
        labelName: "DIST",
        labelColor: "darkBlue",
        labelType: LabelType.DIAMETER,
        xOffset: leftSideXOffset + indentation,
        yOffset: 0.84,
        childYOffset: 3,
        parentOffset: 4,
        childGroup: measurementNames.REIA,
        landmarkIndex: 17,
        landmarkArrayName: constants.dataArrayNames.landmarkIds,
        measurementBounds: MeasurementBounds.DISTANCE_RIGHT,
        secondPlaneChildren: [measurementNames.REIAMid]
    },
    [measurementNames.BoaLength]: {
        labelName: "BOA",
        labelColor: "green",
        labelType: LabelType.LENGTH,
        xOffset: rightSideXOffset,
        yOffset: 0.16,
        landmarkIndex: 1,
        landmarkArrayName: constants.dataArrayNames.landmarkLengths,
        firstPlaneChildren: [
            measurementNames.Renal,
            ...(arrayDifference(renalLengths, [measurementNames.BoaLength])),
            measurementNames.RenalPlus5,
            measurementNames.RenalPlus10,
            measurementNames.RenalPlus15,
            measurementNames.RenalPlus20
        ],
        secondPlaneChildren: [measurementNames.BoaDiameter],
        measurementBounds: [MeasurementBounds.DISTANCE_TRUNK, MeasurementBounds.DISTANCE_TRUNK_CUSP],
        canBeDisabled: true
    },
    [measurementNames.BoaDiameter]: {
        labelName: "BOA",
        labelColor: "blue",
        labelType: LabelType.DIAMETER,
        xOffset: rightSideXOffset,
        yOffset: 0.21,
        landmarkIndex: 6,
        landmarkArrayName: constants.dataArrayNames.landmarkIds,
        secondPlaneChildren: [measurementNames.BoaLength],
        measurementBounds: MeasurementBounds.DISTANCE_TRUNK_CUSP,
        canBeDisabled: true
    },
    [measurementNames.BoaDiameterMax]:
    {
        labelName: "Max D",
        labelColor: "blue",
        labelType: LabelType.DIAMETER,
        xOffset: rightSideXOffset + indentation,
        yOffset: 0.26,
        landmarkIndex: 7,
        landmarkArrayName: constants.dataArrayNames.landmarkIds,
        measurementBounds: MeasurementBounds.DISTANCE_TRUNK_CUSP,
        displayMeasurementType: DisplayMeasurementType.MAX,
        canBeDisabled: true
    },
    [measurementNames.BoaDiameterBif]:
    {
        labelName: "Bifurc",
        labelColor: "blue",
        labelType: LabelType.DIAMETER,
        xOffset: rightSideXOffset + indentation,
        yOffset: 0.31,
        landmarkIndex: 8,
        landmarkArrayName: constants.dataArrayNames.landmarkIds,
        measurementBounds: MeasurementBounds.DISTANCE_TRUNK_CUSP,
        secondPlaneChildren: [measurementNames.Bifurc],
        displayMeasurementType: DisplayMeasurementType.LUMEN,
        centerlineType: CenterlineType.LUMEN
    },
    [measurementNames.Left]: {
        labelName: "Left",
        labelColor: "green",
        labelType: LabelType.LENGTH,
        xOffset: rightSideXOffset,
        yOffset: 0.36,
        landmarkIndex: 3,
        landmarkArrayName: constants.dataArrayNames.landmarkLengths,
        firstPlaneChildren: [
            measurementNames.Renal,
            ...(arrayDifference(renalLengths, [measurementNames.Left])),
            measurementNames.RenalPlus5,
            measurementNames.RenalPlus10,
            measurementNames.RenalPlus15,
            measurementNames.RenalPlus20
        ],
        secondPlaneChildren: [
            measurementNames.LCIADist,
            measurementNames.LCIAMinus10,
            measurementNames.LCIAMinus20,
            measurementNames.LCIAMid,
            measurementNames.LCIALength
        ],
        measurementBounds: [MeasurementBounds.DISTANCE_TRUNK, MeasurementBounds.DISTANCE_LEFT]
    },
    [measurementNames.LCIA]: {
        labelName: "LCIA",
        labelColor: "blue",
        labelType: LabelType.DIAMETER,
        xOffset: rightSideXOffset,
        yOffset: 0.41,
        parentGroup: measurementNames.LCIA,
        landmarkIndex: 18,
        landmarkArrayName: constants.dataArrayNames.landmarkIds,
        measurementBounds: MeasurementBounds.DISTANCE_LEFT,
        firstPlaneChildren: [measurementNames.LCIAMid, measurementNames.LCIALength]
    },
    [measurementNames.LCIAMid]:
    {
        labelName: "MID",
        labelColor: "darkBlue",
        labelType: LabelType.MIDPOINT_DIAMETER,
        xOffset: rightSideXOffset + indentation,
        yOffset: 0.45,
        childYOffset: 1,
        childGroup: measurementNames.LCIA,
        landmarkIndex: 19,
        landmarkArrayName: constants.dataArrayNames.landmarkIds,
        measurementBounds: MeasurementBounds.DISTANCE_LEFT,
        midpointBounds: [measurementNames.LCIA, measurementNames.LCIAMinus20]
    },
    [measurementNames.LCIAMinus20]:
    {
        labelName: "20mm",
        labelColor: "darkBlue",
        labelType: LabelType.OFFSET_DIAMETER,
        xOffset: rightSideXOffset + indentation,
        yOffset: 0.49,
        childYOffset: 2,
        offsetAmount: -20,
        childGroup: measurementNames.LCIA,
        landmarkIndex: 20,
        landmarkArrayName: constants.dataArrayNames.landmarkIds,
        measurementBounds: MeasurementBounds.DISTANCE_LEFT,
        offsetMeasurementName: measurementNames.LCIADist
    },
    [measurementNames.LCIAMinus10]:
    {
        labelName: "10mm",
        labelColor: "darkBlue",
        labelType: LabelType.OFFSET_DIAMETER,
        xOffset: rightSideXOffset + indentation,
        yOffset: 0.53,
        childYOffset: 3,
        offsetAmount: -10,
        childGroup: measurementNames.LCIA,
        landmarkIndex: 21,
        landmarkArrayName: constants.dataArrayNames.landmarkIds,
        measurementBounds: MeasurementBounds.DISTANCE_LEFT,
        offsetMeasurementName: measurementNames.LCIADist
    },
    [measurementNames.LCIADist]:
    {
        labelName: "DIST",
        labelColor: "darkBlue",
        labelType: LabelType.DIAMETER,
        xOffset: rightSideXOffset + indentation,
        yOffset: 0.57,
        childYOffset: 4,
        childGroup: measurementNames.LCIA,
        landmarkIndex: 22,
        landmarkArrayName: constants.dataArrayNames.landmarkIds,
        firstPlaneChildren: [measurementNames.LCIAMinus20, measurementNames.LCIAMinus10],
        secondPlaneChildren: [measurementNames.Left, measurementNames.LCIAMid, measurementNames.LCIALength],
        measurementBounds: MeasurementBounds.DISTANCE_LEFT
    },
    [measurementNames.LCIALength]: {
        labelName: "LCIA",
        labelColor: "green",
        labelType: LabelType.LENGTH,
        xOffset: rightSideXOffset,
        yOffset: 0.62,
        landmarkIndex: 5,
        landmarkArrayName: constants.dataArrayNames.landmarkLengths,
        firstPlaneChildren: [measurementNames.LCIA, measurementNames.LCIAMid],
        secondPlaneChildren: [measurementNames.LCIADist, measurementNames.LCIAMinus20, measurementNames.LCIAMinus10, measurementNames.LCIAMid, measurementNames.Left],
        measurementBounds: [MeasurementBounds.DISTANCE_LEFT]
    },
    [measurementNames.LEIA]: {
        labelName: "LEIA",
        labelColor: "blue",
        labelType: LabelType.DIAMETER,
        xOffset: rightSideXOffset,
        yOffset: 0.67,
        parentGroup: measurementNames.LEIA,
        landmarkIndex: 23,
        landmarkArrayName: constants.dataArrayNames.landmarkIds,
        measurementBounds: MeasurementBounds.DISTANCE_LEFT,
        firstPlaneChildren: [measurementNames.LEIAMid]
    },
    [measurementNames.LEIAMid]:
    {
        labelName: "MID",
        labelColor: "darkBlue",
        labelType: LabelType.MIDPOINT_DIAMETER,
        xOffset: rightSideXOffset + indentation,
        yOffset: 0.71,
        childYOffset: 1,
        childGroup: measurementNames.LEIA,
        landmarkIndex: 24,
        landmarkArrayName: constants.dataArrayNames.landmarkIds,
        measurementBounds: MeasurementBounds.DISTANCE_LEFT,
        midpointBounds: [measurementNames.LEIA, measurementNames.LEIADist]
    },
    [measurementNames.LEIAMin]:
    {
        labelName: "MIN",
        labelColor: "darkBlue",
        labelType: LabelType.DIAMETER,
        xOffset: rightSideXOffset + indentation,
        yOffset: 0.75,
        childYOffset: 2,
        childGroup: measurementNames.LEIA,
        landmarkIndex: 25,
        landmarkArrayName: constants.dataArrayNames.landmarkIds,
        measurementBounds: MeasurementBounds.DISTANCE_LEFT,
        canBeDisabled: true
    },
    [measurementNames.LEIADist]:
    {
        labelName: "DIST",
        labelColor: "darkBlue",
        labelType: LabelType.DIAMETER,
        xOffset: rightSideXOffset + indentation,
        yOffset: 0.79,
        childYOffset: 3,
        landmarkIndex: 26,
        childGroup: measurementNames.LEIA,
        landmarkArrayName: constants.dataArrayNames.landmarkIds,
        measurementBounds: MeasurementBounds.DISTANCE_LEFT,
        secondPlaneChildren: [measurementNames.LEIAMid]
    },
    [measurementNames.RenalCArm]: {
        labelName: "REN C-ARM",
        labelColor: "orange",
        labelType: LabelType.CARM,
        landmarkIndex: 0,
        xOffset: 0.40,
        yOffset: 0.03,
        measurementFocus: measurementNames.Renal
    },
    [measurementNames.LIIACArm]: {
        labelName: "LIIA C-ARM",
        labelColor: "orange",
        labelType: LabelType.CARM,
        landmarkIndex: 2,
        xOffset: 0.50,
        yOffset: 0.95,
        measurementFocus: measurementNames.LCIADist
    },
    [measurementNames.RIIACArm]: {
        labelName: "RIIA C-ARM",
        labelColor: "orange",
        labelType: LabelType.CARM,
        landmarkIndex: 1,
        xOffset: 0.025,
        yOffset: 0.95,
        measurementFocus: measurementNames.RCIADist
    }
};

/**
 * Computes the on-screen pixel location corresponding to the given world coordinates.
 *
 * @param {vtkRenderer} renderer - The VTK renderer.
 * @param {object} worldCoords - The world coordinates { x: number, y: number, z: number }.
 * @param {number} x - The x-coordinate.
 * @param {number} y - The y-coordinate.
 * @param {number} z - The z-coordinate.
 * @returns {Point2D} The on-screen pixel location { x: number, y: number }.
 */
function computeScreenCoordinates(renderer, coords) {
    const [x, y, z] = coords;
    const coordinate = vtkCoordinate.newInstance();
    coordinate.setCoordinateSystemToWorld();
    coordinate.setValue([x, y, z]);
    const displayCoords = coordinate.getComputedDisplayValue(renderer);

    // The y-coordinate is inverted in VTK relative to HTML/CSS, so we need to correct it.
    const renderWindow = renderer.getRenderWindow();
    const canvas = renderWindow.getViews()[0].getCanvas();
    const scale = 1 / window.devicePixelRatio;
    const invertedY = canvas.clientHeight - (displayCoords[1] * scale);
    return { x: displayCoords[0] * scale, y: invertedY };
}

/**
 * Screenshot type enum for camera adjustment logic. Corresponds to CompositeScreenshotType in VisualizationBindings.fs.
 */
const ScreenshotType = Object.freeze({
    RENAL: "Renal",
    TRUNK: "Trunk",
    LEFT_ILIAC: "LeftIliac",
    RIGHT_ILIAC: "RightIliac"
});

/**
 * Gets the indices of all rings currently added to the renderer.
 * @param {Map} diameterRings - The map of diameter rings.
 * @param {vtkRenderer} renderer - The VTK renderer.
 * @returns {number[]} Array of ring indices currently in the renderer.
 */
function getDisplayedRingIndices(diameterRings, renderer) {
    const displayedIndices = [];
    const actors = renderer.getActors();
    diameterRings.forEach((ring, index) => {
        if (actors.includes(ring.actor)) {
            displayedIndices.push(index);
        }
    });
    return displayedIndices;
}

/**
 * Computes the screen-space bounding box of all displayed rings.
 * @param {Map} diameterRings - The map of diameter rings.
 * @param {vtkRenderer} renderer - The VTK renderer.
 * @param {number[]} [activeRingIndices] - Optional array of ring indices to include. If provided, only these rings are considered.
 * @returns {{minX: number, maxX: number, minY: number, maxY: number} | null} The bounding box or null if no rings.
 */
function computeDisplayedRingsBoundingBox(diameterRings, renderer, activeRingIndices = null) {
    let minX = Infinity;
    let maxX = -Infinity;
    let minY = Infinity;
    let maxY = -Infinity;
    let hasRings = false;

    diameterRings.forEach((ring, index) => {
        // If activeRingIndices is provided, only include those rings.
        if (activeRingIndices !== null && !activeRingIndices.includes(index)) {
            return;
        }

        // Check if the ring actor is currently added to the renderer.
        const actors = renderer.getActors();
        if (!actors.includes(ring.actor)) {
            return;
        }

        hasRings = true;
        const ringPoints = ring.dataSource.getPoints().getTuples();
        const chunk = ring.dataSource.getPoints().getNumberOfComponents();

        for (let i = 0; i < ringPoints.length; i += chunk) {
            const worldCoords = [ringPoints[i], ringPoints[i + 1], ringPoints[i + 2]];
            const screenCoords = computeScreenCoordinates(renderer, worldCoords);

            minX = Math.min(minX, screenCoords.x);
            maxX = Math.max(maxX, screenCoords.x);
            minY = Math.min(minY, screenCoords.y);
            maxY = Math.max(maxY, screenCoords.y);
        }
    });

    if (!hasRings) {
        return null;
    }

    return { minX, maxX, minY, maxY };
}

/**
 * Updates bounding box coordinates with a new rectangle.
 * @param {Object} bounds - The bounding box to update (minX, maxX, minY, maxY).
 * @param {Object} rect - The rectangle with x, y, width, and height properties.
 */
function updateBoundingBox(bounds, rect) {
    bounds.minX = Math.min(bounds.minX, rect.x);
    bounds.maxX = Math.max(bounds.maxX, rect.x + rect.width);
    bounds.minY = Math.min(bounds.minY, rect.y);
    bounds.maxY = Math.max(bounds.maxY, rect.y + rect.height);
}

/**
 * Gets the bounding box for a specific CArm label.
 * @param {Object} konvaElements - The Konva elements.
 * @param {string} cArmName - The CArm measurement name.
 * @returns {{minX: number, maxX: number, minY: number, maxY: number} | null} The bounding box or null.
 */
function getCArmLabelBoundingBox(konvaElements, cArmName) {
    const elements = konvaElements[cArmName];
    if (!elements?.stentLabel) {
        return null;
    }

    const labelRect = elements.stentLabel.getClientRect();
    const valueRect = elements.valueLabel?.getClientRect();

    const bounds = {
        minX: labelRect.x,
        maxX: labelRect.x + labelRect.width,
        minY: labelRect.y,
        maxY: labelRect.y + labelRect.height
    };

    if (valueRect) {
        updateBoundingBox(bounds, valueRect);
    }

    return bounds;
}

/**
 * Checks if two bounding boxes overlap.
 * @param {Object} box1 - The first bounding box.
 * @param {Object} box2 - The second bounding box.
 * @returns {boolean} True if the boxes overlap.
 */
function boundingBoxesOverlap(box1, box2) {
    if (!box1 || !box2) {
        return false;
    }
    return !(
        box1.maxX < box2.minX ||
        box1.minX > box2.maxX ||
        box1.maxY < box2.minY ||
        box1.minY > box2.maxY
    );
}

/**
 * Gets the bounding box of all visible labels that match the given filter.
 * @param {Object} konvaElements - The Konva elements.
 * @param {string[]} showGroups - The visible label group names.
 * @param {function(Object): boolean} labelFilter - Filter function for label info.
 * @returns {{minX: number, maxX: number, minY: number, maxY: number} | null} The bounding box or null.
 */
function getLabelsBoundingBox(konvaElements, showGroups, labelFilter) {
    const bounds = {
        minX: Infinity,
        maxX: -Infinity,
        minY: Infinity,
        maxY: -Infinity
    };
    let hasLabels = false;

    // Iterate over all measurements and check if they belong to a visible group.
    for (const measurementName in measurementLabelInfo) {
        const labelInfo = measurementLabelInfo[measurementName];

        // Skip labels that don't match the filter.
        if (!labelInfo || !labelFilter(labelInfo)) {
            continue;
        }

        // Skip CArm labels - they are handled separately.
        if (labelInfo.labelType === LabelType.CARM) {
            continue;
        }

        // Check if this measurement or its parent/child group is in showGroups.
        const belongsToVisibleGroup =
            showGroups.includes(measurementName) ||
            showGroups.includes(labelInfo.parentGroup) ||
            showGroups.includes(labelInfo.childGroup);

        if (!belongsToVisibleGroup) {
            continue;
        }

        const elements = konvaElements[measurementName];
        if (!elements) {
            continue;
        }

        // Check stentLabel.
        if (elements.stentLabel) {
            hasLabels = true;
            const rect = elements.stentLabel.getClientRect();
            updateBoundingBox(bounds, rect);
        }

        // Check valueLabel.
        if (elements.valueLabel) {
            const rect = elements.valueLabel.getClientRect();
            updateBoundingBox(bounds, rect);
        }

        // Check visibilityLabel.
        if (elements.visibilityLabel) {
            const rect = elements.visibilityLabel.getClientRect();
            updateBoundingBox(bounds, rect);
        }
    }

    if (!hasLabels) {
        return null;
    }

    return bounds;
}

/**
 * Gets the bounding box of all visible labels on the left side of the viewport.
 * @param {Object} konvaElements - The Konva elements.
 * @param {string[]} showGroups - The visible label group names.
 * @returns {{minX: number, maxX: number, minY: number, maxY: number} | null} The bounding box or null.
 */
function getLeftSideLabelsBoundingBox(konvaElements, showGroups) {
    return getLabelsBoundingBox(konvaElements, showGroups, labelInfo => labelInfo.xOffset < rightSideXOffset);
}

/**
 * Gets the bounding box of all visible labels on the right side of the viewport.
 * @param {Object} konvaElements - The Konva elements.
 * @param {string[]} showGroups - The visible label group names.
 * @returns {{minX: number, maxX: number, minY: number, maxY: number} | null} The bounding box or null.
 */
function getRightSideLabelsBoundingBox(konvaElements, showGroups) {
    return getLabelsBoundingBox(konvaElements, showGroups, labelInfo => labelInfo.xOffset >= rightSideXOffset);
}

/**
 * Handles camera adjustment for single-sided label layouts (labels on one side, rings on the other).
 * @param {Object} ringBounds - The ring bounding box.
 * @param {Object} labelBox - The label bounding box to check for overlap.
 * @param {Object} cArmLabelBox - The CArm label bounding box.
 * @param {Object} paddedRingBounds - The padded ring bounding box.
 * @param {boolean} isRightSide - True if rings should be on the right side, false if on the left.
 * @param {number} padding - The padding in pixels.
 * @param {number} viewportWidth - The viewport width in pixels.
 * @param {number} ringWidth - The width of the rings.
 * @param {number} ringHeight - The height of the rings.
 * @param {boolean} cArmOverlapIsTop - True if CArm overlap check is for top edge, false for bottom.
 * @returns {{panAmountX: number, zoomAdjustment: number, needsAdjustment: boolean}} The adjustment values.
 */
function handleSingleSidedLayout(
    ringBounds,
    labelBox,
    cArmLabelBox,
    paddedRingBounds,
    isRightSide,
    padding,
    viewportWidth,
    ringWidth,
    ringHeight,
    cArmOverlapIsTop
) {
    let needsAdjustment = false;
    let panAmountX = 0;
    let zoomAdjustment = 1.0;

    const edgeLimit = isRightSide ? (viewportWidth - padding) : padding;

    // Check if rings overlap with labels using bounding boxes.
    if (labelBox && boundingBoxesOverlap(paddedRingBounds, labelBox)) {
        const desiredPan = isRightSide
            ? (labelBox.maxX + padding) - ringBounds.minX
            : (labelBox.minX - padding) - ringBounds.maxX;

        // Check if panning would push rings off the edge.
        const projectedEdge = isRightSide
            ? ringBounds.maxX + desiredPan
            : ringBounds.minX + desiredPan;

        const wouldExceedEdge = isRightSide
            ? projectedEdge > edgeLimit
            : projectedEdge < edgeLimit;

        if (wouldExceedEdge) {
            // Can't pan enough, need to zoom out.
            const availableWidth = isRightSide
                ? edgeLimit - (labelBox.maxX + padding)
                : (labelBox.minX - padding) - edgeLimit;

            if (ringWidth > availableWidth && availableWidth > 0) {
                const proposedZoom = availableWidth / ringWidth * 0.9;
                if (proposedZoom > 0.3 && proposedZoom < 1.0) {
                    zoomAdjustment = Math.min(zoomAdjustment, proposedZoom);
                    needsAdjustment = true;
                }
            }

            // Pan as much as we can.
            panAmountX = isRightSide
                ? edgeLimit - ringBounds.maxX
                : edgeLimit - ringBounds.minX;
        } else {
            panAmountX = desiredPan;
        }

        const panThreshold = isRightSide ? 1 : -Infinity;
        if ((isRightSide && panAmountX > 1) || (!isRightSide && Math.abs(panAmountX) > 1)) {
            needsAdjustment = true;
        }
    }

    // Check if CArm label overlaps with rings.
    if (cArmLabelBox && boundingBoxesOverlap(paddedRingBounds, cArmLabelBox)) {
        const overlapY = cArmOverlapIsTop
            ? (cArmLabelBox.maxY + padding) - ringBounds.minY
            : ringBounds.maxY - (cArmLabelBox.minY - padding);

        if (overlapY > 0) {
            const proposedZoom = (ringHeight - overlapY) / ringHeight;
            if (proposedZoom > 0.3 && proposedZoom < 1.0) {
                zoomAdjustment = Math.min(zoomAdjustment, proposedZoom);
                needsAdjustment = true;
            }
        }
    }

    return { panAmountX, zoomAdjustment, needsAdjustment };
}

/**
 * Adjusts the camera to prevent label-ring overlap based on screenshot type.
 * @param {Object} context - The Konva context.
 * @param {Object} parentContext - The parent VTK context.
 * @param {Object} meshes - The meshes object.
 * @param {string[]} showGroups - The visible label group names.
 * @param {string} screenshotType - The type of screenshot.
 * @param {number} padding - The padding in pixels.
 */
function adjustCameraForScreenshotType(context, parentContext, meshes, showGroups, screenshotType, padding = 20) {
    const renderer = parentContext.renderer;
    const renderWindow = parentContext.renderWindow;
    const camera = renderer.getActiveCamera();
    const konvaElements = context.konvaElements;

    // Get viewport dimensions.
    const vtkCanvas = parentContext.genericRenderWindow.getApiSpecificRenderWindow().getCanvas();
    const dpr = window.devicePixelRatio || 1;
    const viewportWidth = vtkCanvas.width / dpr;
    const viewportHeight = vtkCanvas.height / dpr;

    // Get the indices of rings currently displayed in the renderer.
    const displayedRingIndices = getDisplayedRingIndices(meshes.diameterRings, renderer);
    if (displayedRingIndices.length === 0) {
        return;
    }

    // Get label bounding boxes based on screenshot type.
    const leftLabelBox = getLeftSideLabelsBoundingBox(konvaElements, showGroups);
    const rightLabelBox = getRightSideLabelsBoundingBox(konvaElements, showGroups);
    const cArmLabelBox = getCArmLabelBoundingBoxForType(konvaElements, screenshotType);

    const maxIterations = 10;
    let iteration = 0;
    const renalPadding = 150;

    while (iteration < maxIterations) {
        iteration++;
        let needsAdjustment = false;
        let panAmountX = 0;
        let panAmountY = 0;
        let zoomAdjustment = 1.0;

        const ringBounds = computeDisplayedRingsBoundingBox(meshes.diameterRings, renderer, displayedRingIndices);

        if (!ringBounds) {
            break;
        }

        const ringWidth = ringBounds.maxX - ringBounds.minX;
        const ringHeight = ringBounds.maxY - ringBounds.minY;
        const ringCenterX = (ringBounds.minX + ringBounds.maxX) / 2;
        const ringCenterY = (ringBounds.minY + ringBounds.maxY) / 2;

        // Create padded bounding boxes for overlap detection.
        const paddedRingBounds = {
            minX: ringBounds.minX - padding,
            maxX: ringBounds.maxX + padding,
            minY: ringBounds.minY - padding,
            maxY: ringBounds.maxY + padding
        };

        switch (screenshotType) {
            case ScreenshotType.RENAL: {
                // Rings should be on the right side (labels on left).
                const result = handleSingleSidedLayout(
                    ringBounds,
                    leftLabelBox,
                    cArmLabelBox,
                    {
                        minX: ringBounds.minX - renalPadding,
                        maxX: ringBounds.maxX + renalPadding,
                        minY: ringBounds.minY - renalPadding,
                        maxY: ringBounds.maxY + renalPadding
                    },
                    true, // isRightSide
                    renalPadding,
                    viewportWidth,
                    ringWidth,
                    ringHeight,
                    true // cArmOverlapIsTop
                );
                panAmountX = result.panAmountX;
                zoomAdjustment = result.zoomAdjustment;
                needsAdjustment = result.needsAdjustment;
                break;
            }

            case ScreenshotType.TRUNK: {
                // Labels on both sides, center the rings.
                const leftEdge = leftLabelBox ? leftLabelBox.maxX + padding : padding;
                const rightEdge = rightLabelBox ? rightLabelBox.minX - padding : viewportWidth - padding;
                const availableWidth = rightEdge - leftEdge;

                // Check if rings fit in the available space horizontally.
                if (ringWidth > availableWidth && availableWidth > 0) {
                    const proposedZoom = availableWidth / ringWidth * 0.9;
                    if (proposedZoom > 0.3 && proposedZoom < 1.0) {
                        zoomAdjustment = Math.min(zoomAdjustment, proposedZoom);
                        needsAdjustment = true;
                    }
                }

                // Check if rings fit in the available space vertically.
                if (ringBounds.minY < padding || ringBounds.maxY > (viewportHeight - padding)) {
                    const availableHeight = viewportHeight - (2 * padding);

                    if (ringHeight > availableHeight && availableHeight > 0) {
                        // Rings are too tall - need to zoom out.
                        const proposedZoomVertical = availableHeight / ringHeight * 0.9;
                        if (proposedZoomVertical > 0.3 && proposedZoomVertical < 1.0) {
                            zoomAdjustment = Math.min(zoomAdjustment, proposedZoomVertical);
                            needsAdjustment = true;
                        }
                    } else {
                        // Rings would fit if centered vertically.
                        const targetCenterY = viewportHeight / 2;
                        if (Math.abs(ringCenterY - targetCenterY) > 5) {
                            panAmountY = targetCenterY - ringCenterY;
                            needsAdjustment = true;
                        }
                    }
                }

                // Center the rings horizontally between the labels.
                const targetCenterX = (leftEdge + rightEdge) / 2;
                if (Math.abs(ringCenterX - targetCenterX) > 5) {
                    panAmountX = targetCenterX - ringCenterX;
                    needsAdjustment = true;
                }

                break;
            }

            case ScreenshotType.LEFT_ILIAC: {
                // Rings should be on the left side (labels on right).
                const result = handleSingleSidedLayout(
                    ringBounds,
                    rightLabelBox,
                    cArmLabelBox,
                    paddedRingBounds,
                    false, // isRightSide
                    padding,
                    viewportWidth,
                    ringWidth,
                    ringHeight,
                    false // cArmOverlapIsTop
                );

                panAmountX = result.panAmountX;
                zoomAdjustment = result.zoomAdjustment;
                needsAdjustment = result.needsAdjustment;
                break;
            }

            case ScreenshotType.RIGHT_ILIAC: {
                // Rings should be on the right side (labels on left).
                const result = handleSingleSidedLayout(
                    ringBounds,
                    leftLabelBox,
                    cArmLabelBox,
                    paddedRingBounds,
                    true, // isRightSide
                    padding,
                    viewportWidth,
                    ringWidth,
                    ringHeight,
                    false // cArmOverlapIsTop
                );

                panAmountX = result.panAmountX;
                zoomAdjustment = result.zoomAdjustment;
                needsAdjustment = result.needsAdjustment;
                break;
            }
        }

        if (!needsAdjustment) {
            break;
        }

        // Apply zoom adjustment if needed (accept zoom factors down to 0.3).
        if (zoomAdjustment < 1.0 && zoomAdjustment > 0.3) {
            vtkInteractorStyleManipulator.dollyByFactor(
                renderWindow.getInteractor(),
                renderer,
                zoomAdjustment
            );
            renderer.resetCameraClippingRange();
            renderWindow.render();
        }

        // Apply pan adjustment if needed.
        if (Math.abs(panAmountX) > 1 || Math.abs(panAmountY) > 1) {
            panCamera(camera, renderer, panAmountX, panAmountY);
            renderer.resetCameraClippingRange();
            renderWindow.render();
        }
    }

    // Second pass: Check if there's too much gap between labels and rings.
    // We want to maintain the padding distance between them.
    const finalRingBounds = computeDisplayedRingsBoundingBox(meshes.diameterRings, renderer, displayedRingIndices);

    if (finalRingBounds) {
        let gapAdjustmentNeeded = false;
        let gapPanAmountX = 0;

        // Helper function to compute gap adjustment for single-sided layouts.
        const computeGapAdjustment = (labelBox, isRightSide, padding) => {
            if (!labelBox) {
                return;
            }

            const gap = isRightSide
                ? finalRingBounds.minX - labelBox.maxX
                : labelBox.minX - finalRingBounds.maxX;

            // If the gap is larger than padding, bring them closer to maintain the padding distance.
            if (gap > padding) {
                gapPanAmountX = isRightSide ? -(gap - padding) : (gap - padding);
                gapAdjustmentNeeded = true;
            }
        };

        switch (screenshotType) {
            case ScreenshotType.RENAL:
                computeGapAdjustment(leftLabelBox, true, renalPadding);
                break;

            case ScreenshotType.TRUNK:
                // For trunk, center the rings between labels.
                // Gap adjustment is already handled by centering logic.
                break;

            case ScreenshotType.LEFT_ILIAC:
                computeGapAdjustment(rightLabelBox, false, padding);
                break;

            case ScreenshotType.RIGHT_ILIAC:
                computeGapAdjustment(leftLabelBox, true, padding);
                break;
        }

        if (gapAdjustmentNeeded && Math.abs(gapPanAmountX) > 1) {
            panCamera(camera, renderer, gapPanAmountX, 0);
            renderer.resetCameraClippingRange();
            renderWindow.render();
        }
    }
}

/**
 * Gets the CArm label bounding box for the given screenshot type.
 * @param {Object} konvaElements - The Konva elements.
 * @param {string} screenshotType - The screenshot type.
 * @returns {Object | null} The bounding box or null.
 */
function getCArmLabelBoundingBoxForType(konvaElements, screenshotType) {
    switch (screenshotType) {
        case ScreenshotType.RENAL:
            return getCArmLabelBoundingBox(konvaElements, measurementNames.RenalCArm);
        case ScreenshotType.LEFT_ILIAC:
            return getCArmLabelBoundingBox(konvaElements, measurementNames.LIIACArm);
        case ScreenshotType.RIGHT_ILIAC:
            return getCArmLabelBoundingBox(konvaElements, measurementNames.RIIACArm);
        case ScreenshotType.TRUNK:
        default:
            return null;
    }
}

/**
 * Pans the camera by the given screen-space pixel amounts.
 *
 * This implementation manually computes the pan transformation because vtk.js
 * does not provide a built-in display-to-world coordinate conversion or camera
 * panning utility. The vtkCoordinate class only supports world-to-display conversion.
 *
 * @param {vtkCamera} camera - The VTK camera.
 * @param {vtkRenderer} renderer - The VTK renderer.
 * @param {number} deltaX - The horizontal pan amount in pixels.
 * @param {number} deltaY - The vertical pan amount in pixels.
 */
function panCamera(camera, renderer, deltaX, deltaY) {
    const focalPoint = camera.getFocalPoint();
    const position = camera.getPosition();
    const viewUp = camera.getViewUp();

    // Get the direction of projection from the camera.
    const dop = camera.getDirectionOfProjection();

    // Calculate the right vector (perpendicular to view up and direction of projection).
    const right = [0, 0, 0];
    vtkMath.cross(dop, viewUp, right);
    vtkMath.normalize(right);

    // Calculate the actual up vector (orthogonal to both right and dop).
    // We recompute up instead of using viewUp directly to ensure perfect orthogonality.
    // The camera's viewUp may not be perfectly perpendicular to dop due to floating-point
    // precision or camera manipulations. This Gram-Schmidt-style orthogonalization
    // guarantees a perfectly orthonormal coordinate system for accurate panning.
    const up = [0, 0, 0];
    vtkMath.cross(right, dop, up);
    vtkMath.normalize(up);

    // Get viewport dimensions to calculate world-space movement.
    const renderWindow = renderer.getRenderWindow();
    const view = renderWindow.getViews()[0];
    const size = view.getSize();
    const viewportWidth = size[0];
    const viewportHeight = size[1];

    // Calculate view height in world coordinates.
    let viewHeight;
    if (camera.getParallelProjection()) {
        viewHeight = camera.getParallelScale() * 2;
    } else {
        const distance = vtkMath.distance2BetweenPoints(position, focalPoint);
        const viewAngle = camera.getViewAngle();
        viewHeight = 2 * Math.sqrt(distance) * Math.tan(vtkMath.radiansFromDegrees(viewAngle / 2));
    }

    // Calculate view width based on aspect ratio.
    const aspectRatio = viewportWidth / viewportHeight;
    const viewWidth = viewHeight * aspectRatio;

    // Calculate pixels to world scale for each axis.
    const pixelToWorldX = viewWidth / viewportWidth;
    const pixelToWorldY = viewHeight / viewportHeight;

    // Calculate the world-space pan amounts.
    const worldDeltaX = -deltaX * pixelToWorldX;
    const worldDeltaY = deltaY * pixelToWorldY;

    // Calculate the pan vector in world space.
    const panVector = [
        right[0] * worldDeltaX + up[0] * worldDeltaY,
        right[1] * worldDeltaX + up[1] * worldDeltaY,
        right[2] * worldDeltaX + up[2] * worldDeltaY
    ];

    // Apply the pan to both focal point and position to maintain camera orientation.
    const newFocalPoint = [0, 0, 0];
    const newPosition = [0, 0, 0];
    vtkMath.add(focalPoint, panVector, newFocalPoint);
    vtkMath.add(position, panVector, newPosition);

    camera.setFocalPoint(...newFocalPoint);
    camera.setPosition(...newPosition);
}

/**
 * Returns the master branch index bounds for the given centerline field data.
 * @param {Object} centerlineFieldData
 * @returns {import('../CenterlineMeasurement/CenterlinePlaneUpdater').BranchIndexBounds}
 */
export function getMasterBranchIndexBounds(centerlineFieldData) {
    const [transitionTrunkMin, transitionTrunkMax] = centerlineFieldData.getArrayByName("Transition_Branch_Trunk").getRange();
    const [transitionLeftMin, transitionLeftMax] = centerlineFieldData.getArrayByName("Transition_Branch_Left").getRange();
    const [transitionRightMin, transitionRightMax] = centerlineFieldData.getArrayByName("Transition_Branch_Right").getRange();
    const [distanceTrunkCuspMin, distanceTrunkCuspMax] = centerlineFieldData.getArrayByName("Distance_Branch_Trunk_Cusp").getRange();
    const [distanceTrunkMin, distanceTrunkMax] = centerlineFieldData.getArrayByName("Distance_Branch_Trunk").getRange();
    const [distanceLeftMin, distanceLeftMax] = centerlineFieldData.getArrayByName("Distance_Branch_Left").getRange();
    const [distanceRightMin, distanceRightMax] = centerlineFieldData.getArrayByName("Distance_Branch_Right").getRange();

    return {
        transitionTrunk: { firstIndex: transitionTrunkMin, lastIndex: transitionTrunkMax },
        transitionLeftBranch: { firstIndex: transitionLeftMin, lastIndex: transitionLeftMax },
        transitionRightBranch: { firstIndex: transitionRightMin, lastIndex: transitionRightMax },
        distanceTrunk: { firstIndex: distanceTrunkMin, lastIndex: distanceTrunkMax },
        distanceLeftBranch: { firstIndex: distanceLeftMin, lastIndex: distanceLeftMax },
        distanceRightBranch: { firstIndex: distanceRightMin, lastIndex: distanceRightMax },
        distanceTrunkCusp: { firstIndex: distanceTrunkCuspMin, lastIndex: distanceTrunkCuspMax }
    };
}

export function convertDistanceUnifiedBranchIndexToMasterIndex(branchIndex, branchSelection, centerlineFieldData) {
    const [distanceTrunkCuspMin, distanceTrunkCuspMax] = centerlineFieldData.getArrayByName("Distance_Branch_Trunk_Cusp").getRange();
    const [distanceTrunkMin, distanceTrunkMax] = centerlineFieldData.getArrayByName("Distance_Branch_Trunk").getRange();
    const [distanceLeftMin, distanceLeftMax] = centerlineFieldData.getArrayByName("Distance_Branch_Left").getRange();
    const [distanceRightMin, distanceRightMax] = centerlineFieldData.getArrayByName("Distance_Branch_Right").getRange();

    if (branchSelection.name === "Cusp") {
        return branchIndex;
    } else if (branchSelection.name === "Left") {
        if (branchIndex <= distanceTrunkMax) {
            return branchIndex;
        } else {
            return (branchIndex - distanceTrunkMax - 1) + distanceLeftMin;
        }
    } else if (branchSelection.name === "Right") {
        if (branchIndex <= distanceTrunkMax) {
            return branchIndex;
        } else {
            return (branchIndex - distanceTrunkMax - 1) + distanceRightMin;
        }
    }
    console.error("Unable to convert branch index to master index: ", branchIndex, branchSelection);

    // Fallback to avoid a crash
    return branchIndex;
}

export function convertUnifiedBranchIndexToMasterIndex(branchIndex, branchSelection, centerlineFieldData) {
    const [distanceTrunkMin, distanceTrunkMax] = centerlineFieldData.getArrayByName("Distance_Branch_Trunk").getRange();
    const [distanceLeftMin, distanceLeftMax] = centerlineFieldData.getArrayByName("Distance_Branch_Left").getRange();
    const [distanceRightMin, distanceRightMax] = centerlineFieldData.getArrayByName("Distance_Branch_Right").getRange();
    const [transitionTrunkMin, transitionTrunkMax] = centerlineFieldData.getArrayByName("Transition_Branch_Trunk").getRange();
    const [transitionLeftMin, transitionLeftMax] = centerlineFieldData.getArrayByName("Transition_Branch_Left").getRange();
    const [transitionRightMin, transitionRightMax] = centerlineFieldData.getArrayByName("Transition_Branch_Right").getRange();

    if (CenterlineBranchSelection.areEqual(branchSelection, CenterlineBranchSelection.DISTANCE_CUSP)) {
        return branchIndex;
    }

    if (CenterlineBranchSelection.areEqual(branchSelection, CenterlineBranchSelection.DISTANCE_LEFT)
        || CenterlineBranchSelection.areEqual(branchSelection, CenterlineBranchSelection.TRANSITION_LEFT)) {
        if (branchIndex <= distanceTrunkMax) {
            return branchIndex;
        } else {
            return (branchIndex - distanceTrunkMax - 1) + distanceLeftMin;
        }
    }

    if (CenterlineBranchSelection.areEqual(branchSelection, CenterlineBranchSelection.DISTANCE_RIGHT)
        || CenterlineBranchSelection.areEqual(branchSelection, CenterlineBranchSelection.TRANSITION_RIGHT)) {
        if (branchIndex <= distanceTrunkMax) {
            return branchIndex;
        } else {
            return (branchIndex - distanceTrunkMax - 1) + distanceRightMin;
        }
    }

    console.error("Unable to convert branch index to master index: ", branchIndex, branchSelection);

    // Fallback to avoid a crash
    return branchIndex;
}

export function convertMasterToDistanceBranchIndex(masterIndex, centerlineFieldData) {
    const [distanceTrunkCuspMin, distanceTrunkCuspMax] = centerlineFieldData.getArrayByName("Distance_Branch_Trunk_Cusp").getRange();
    const [distanceTrunkMin, distanceTrunkMax] = centerlineFieldData.getArrayByName("Distance_Branch_Trunk").getRange();
    const [distanceLeftMin, distanceLeftMax] = centerlineFieldData.getArrayByName("Distance_Branch_Left").getRange();
    const [distanceRightMin, distanceRightMax] = centerlineFieldData.getArrayByName("Distance_Branch_Right").getRange();

    if (masterIndex <= distanceTrunkCuspMax) { return { branch: CenterlineDistanceBranchSelection.CUSP, index: masterIndex }; }
    if (masterIndex >= distanceLeftMin && masterIndex <= distanceLeftMax) { return { branch: CenterlineDistanceBranchSelection.LEFT, index: (masterIndex - distanceLeftMin) + distanceTrunkMax + 1 }; }
    if (masterIndex >= distanceRightMin && masterIndex <= distanceRightMax) { return { branch: CenterlineDistanceBranchSelection.RIGHT, index: (masterIndex - distanceRightMin) + distanceTrunkMax + 1 }; }

    const error = 'Invalid Master Index';
    throw error;
}

export function convertMasterToTransitionBranchIndex(masterIndex, centerlineFieldData) {
    const [transitionTrunkMin, transitionTrunkMax] = centerlineFieldData.getArrayByName("Transition_Branch_Trunk").getRange();
    const [transitionLeftMin, transitionLeftMax] = centerlineFieldData.getArrayByName("Transition_Branch_Left").getRange();
    const [transitionRightMin, transitionRightMax] = centerlineFieldData.getArrayByName("Transition_Branch_Right").getRange();

    if (masterIndex <= transitionTrunkMax) { return { branch: CenterlineTransitionBranchSelection.LEFT, index: masterIndex }; }
    //Master index is in the transition gap
    if (masterIndex > transitionTrunkMax && masterIndex < transitionLeftMin) {
        const transitionGapMin = transitionTrunkMax + 1;
        const transitionGapMax = transitionLeftMin - 1;
        //Closer to the bottom of the gap
        if (Math.abs(masterIndex - transitionGapMin) >= Math.abs(masterIndex - transitionGapMax)) {
            return { branch: CenterlineTransitionBranchSelection.LEFT, index: transitionTrunkMax + 1 };
        } else { //Closer to the top of the gap
            return { branch: CenterlineTransitionBranchSelection.LEFT, index: transitionTrunkMax };
        }
    }
    if (masterIndex >= transitionLeftMin && masterIndex <= transitionLeftMax) { return { branch: CenterlineTransitionBranchSelection.LEFT, index: (masterIndex - transitionLeftMin) + transitionTrunkMax + 1 }; }
    //Master index is in the transition gap
    if (masterIndex > transitionLeftMax && masterIndex < transitionRightMin) {
        const transitionGapMin = transitionLeftMax + 1;
        const transitionGapMax = transitionRightMin - 1;
        //Closer to the bottom of the gap
        if (Math.abs(masterIndex - transitionGapMin) >= Math.abs(masterIndex - transitionGapMax)) {
            return { branch: CenterlineTransitionBranchSelection.RIGHT, index: transitionTrunkMax + 1 };
        } else { //Closer to the top of the gap
            return { branch: CenterlineTransitionBranchSelection.RIGHT, index: transitionTrunkMax };
        }
    }
    if (masterIndex >= transitionRightMin && masterIndex <= transitionRightMax) { return { branch: CenterlineTransitionBranchSelection.RIGHT, index: (masterIndex - transitionRightMin) + transitionTrunkMax + 1 }; }

    const error = 'Invalid Master Index';
    throw error;
}

function indexInBounds(index, bounds, centerlineFieldData) {
    const [transitionTrunkMin, transitionTrunkMax] = centerlineFieldData.getArrayByName("Transition_Branch_Trunk").getRange();
    const [transitionLeftMin, transitionLeftMax] = centerlineFieldData.getArrayByName("Transition_Branch_Left").getRange();
    const [transitionRightMin, transitionRightMax] = centerlineFieldData.getArrayByName("Transition_Branch_Right").getRange();
    const [distanceTrunkMin, distanceTrunkMax] = centerlineFieldData.getArrayByName("Distance_Branch_Trunk").getRange();
    const [distanceTrunkCuspMin, distanceTrunkCuspMax] = centerlineFieldData.getArrayByName("Distance_Branch_Trunk_Cusp").getRange();
    const [distanceLeftMin, distanceLeftMax] = centerlineFieldData.getArrayByName("Distance_Branch_Left").getRange();
    const [distanceRightMin, distanceRightMax] = centerlineFieldData.getArrayByName("Distance_Branch_Right").getRange();
    switch (bounds.head.name) {
        case MeasurementBounds.TRANSITION_TRUNK: {
            const masterIndex = convertUnifiedBranchIndexToMasterIndex(index, CenterlineBranchSelection.TRANSITION_LEFT, centerlineFieldData);
            return masterIndex >= transitionTrunkMin && masterIndex <= transitionTrunkMax;
            break;
        }
        case MeasurementBounds.TRANSITION_LEFT: {
            const masterIndex = convertUnifiedBranchIndexToMasterIndex(index, CenterlineBranchSelection.TRANSITION_LEFT, centerlineFieldData);
            return masterIndex >= transitionLeftMin && masterIndex <= transitionLeftMax;
            break;
        }
        case MeasurementBounds.TRANSITION_RIGHT: {
            const masterIndex = convertUnifiedBranchIndexToMasterIndex(index, CenterlineBranchSelection.TRANSITION_RIGHT, centerlineFieldData);
            return masterIndex >= transitionRightMin && masterIndex <= transitionRightMax;
            break;
        }
        case MeasurementBounds.DISTANCE_TRUNK: {
            const masterIndex = convertUnifiedBranchIndexToMasterIndex(index, CenterlineBranchSelection.DISTANCE_CUSP, centerlineFieldData);
            return masterIndex >= distanceTrunkMin && masterIndex <= distanceTrunkMax;
            break;
        }
        case MeasurementBounds.DISTANCE_TRUNK_CUSP: {
            const masterIndex = convertUnifiedBranchIndexToMasterIndex(index, CenterlineBranchSelection.DISTANCE_CUSP, centerlineFieldData);
            return masterIndex >= distanceTrunkCuspMin && masterIndex <= distanceTrunkCuspMax;
            break;
        }
        case MeasurementBounds.DISTANCE_LEFT: {
            const masterIndex = convertUnifiedBranchIndexToMasterIndex(index, CenterlineBranchSelection.DISTANCE_LEFT, centerlineFieldData);
            return masterIndex >= distanceLeftMin && masterIndex <= distanceLeftMax;
            break;
        }
        case MeasurementBounds.DISTANCE_RIGHT: {
            const masterIndex = convertUnifiedBranchIndexToMasterIndex(index, CenterlineBranchSelection.DISTANCE_RIGHT, centerlineFieldData);
            return masterIndex >= distanceRightMin && masterIndex <= distanceRightMax;
            break;
        }
        default: {
            return false;
        }
    }
    return false;
}

function calcMidPointIndex(i1, i2) {
    const res = (i1 === i2) ? i1 : Math.floor(Math.abs(i2 - i1) / 2) + (Math.min(i1, i2));
    log(`Midpoint index between ${i1} and ${i2} is ${res}`);
    return res;
}

function getCenterlineCoords(centerlineFilter, measurement, renderer) {
    const points = centerlineFilter.getOutputData(0).getPoints();
    log(measurement[0]);
    if (!isNullOrUndefined(measurement[0]?.index)) {
        return computeScreenCoordinates(renderer, points.getTuple(measurement[0].index));
    } else if (!isNullOrUndefined(measurement[0]?.thirdIndex)) {
        return computeScreenCoordinates(renderer, points.getTuple(measurement[0].secondIndex));
    } else if (!isNullOrUndefined(measurement[0]?.firstIndex) && !isNullOrUndefined(measurement[0]?.secondIndex)) {
        const firstIndex = measurement[0].firstIndex;
        const secondIndex = measurement[0].secondIndex;
        const midPointIndex = calcMidPointIndex(firstIndex, secondIndex);
        return computeScreenCoordinates(renderer, points.getTuple(midPointIndex));
    } else {
        throw 'Invalid measurement';
    }
}

/**
 * @param {Meshes} meshes
 * @param {CenterlineType} centerlineType
 */
function getCenterlineDataSource(meshes, centerlineType) {
    if (centerlineType === CenterlineType.LUMEN) {
        return meshes.lumenCenterline.dataSource;
    } else if (centerlineType === CenterlineType.WALL) {
        return meshes.wallCenterline.dataSource;
    } else {
        console.error("Centerline type doesn't exist");
    }
}

/**
 * @param {CenterlineType} centerlineType
 * @param {CenterlineBranchSelection} branchSelection
 * @param {Meshes} meshes
 * @param {FieldDataArrayNames} fieldDataArrayNames
 * @returns {vtkUnifiedBranchFilter}
 */
export function makeRootFilter(centerlineType, branchSelection, meshes, fieldDataArrayNames) {
    const centerlineSource = meshes.wallCenterline.dataSource;
    if (CenterlineBranchSelection.areEqual(branchSelection, CenterlineBranchSelection.DISTANCE_LEFT)) {
        const distanceLeftFilterLumen = createBasePipeline(centerlineSource, fieldDataArrayNames);
        distanceLeftFilterLumen.setCurrentCenterlineBranch(CenterlineBranchSelection.DISTANCE_LEFT);
        return distanceLeftFilterLumen;
    } else if (CenterlineBranchSelection.areEqual(branchSelection, CenterlineBranchSelection.DISTANCE_RIGHT)) {
        const distanceRightFilterLumen = createBasePipeline(centerlineSource, fieldDataArrayNames);
        distanceRightFilterLumen.setCurrentCenterlineBranch(CenterlineBranchSelection.DISTANCE_RIGHT);
        return distanceRightFilterLumen;
    } else if (CenterlineBranchSelection.areEqual(branchSelection, CenterlineBranchSelection.DISTANCE_CUSP)) {
        const distanceCuspFilterLumen = createBasePipeline(centerlineSource, fieldDataArrayNames);
        distanceCuspFilterLumen.setCurrentCenterlineBranch(CenterlineBranchSelection.DISTANCE_CUSP);
        return distanceCuspFilterLumen;
    } else if (CenterlineBranchSelection.areEqual(branchSelection, CenterlineBranchSelection.TRANSITION_LEFT)) {
        const transitionLeftFilterLumen = createBasePipeline(centerlineSource, fieldDataArrayNames);
        transitionLeftFilterLumen.setCurrentCenterlineBranch(CenterlineBranchSelection.TRANSITION_LEFT);
        return transitionLeftFilterLumen;
    } else if (CenterlineBranchSelection.areEqual(branchSelection, CenterlineBranchSelection.TRANSITION_RIGHT)) {
        const transitionRightFilterLumen = createBasePipeline(centerlineSource, fieldDataArrayNames);
        transitionRightFilterLumen.setCurrentCenterlineBranch(CenterlineBranchSelection.TRANSITION_RIGHT);
        return transitionRightFilterLumen;
    } else {
        console.error("Centerline branch doesn't exist");
    }
}

export function getLengthVectorMagnitude(meshes, branch, fieldDataArrayNames, d1Idx, d2Idx) {
    var centerlineBranchSelection;
    if (branch === 'Left') {
        centerlineBranchSelection = CenterlineBranchSelection.DISTANCE_LEFT;
    } else if (branch === 'Right') {
        centerlineBranchSelection = CenterlineBranchSelection.DISTANCE_RIGHT;
    } else if (branch === 'Cusp') {
        centerlineBranchSelection = CenterlineBranchSelection.DISTANCE_CUSP;
    }

    const filter =
        makeRootFilter(
            CenterlineType.WALL,
            centerlineBranchSelection,
            meshes,
            fieldDataArrayNames);
    const pointData = filter.getOutputData(0).getPoints();
    const d1point = pointData.getTuple(d1Idx);
    const d2point = pointData.getTuple(d2Idx);
    const d1vector = Vector3.ofVtk(d1point);
    const d2vector = Vector3.ofVtk(d2point);
    const d1ToD2 = Vector3.subtract(d1vector, d2vector);
    return Vector3.magnitude(d1ToD2);
}

export function getDiameters(meshes, branch, centerlineType, fieldDataArrayNames, pointDataArrayNames, dIdx) {
    var centerlineBranchSelection;
    if (branch === 'Left') {
        centerlineBranchSelection = CenterlineBranchSelection.DISTANCE_LEFT;
    } else if (branch === 'Right') {
        centerlineBranchSelection = CenterlineBranchSelection.DISTANCE_RIGHT;
    } else if (branch === 'Cusp') {
        centerlineBranchSelection = CenterlineBranchSelection.DISTANCE_CUSP;
    }

    var centerlineTypeSelection;
    if (centerlineType === 'Wall') {
        centerlineTypeSelection = CenterlineType.WALL;
    } else if (centerlineType === 'Lumen') {
        centerlineTypeSelection = CenterlineType.LUMEN;
    }

    const filter =
        makeRootFilter(
            centerlineTypeSelection,
            centerlineBranchSelection,
            meshes,
            fieldDataArrayNames);
    const pointData =
        filter.getOutputData(0)
            .getPointData()
            .getArrayByName(pointDataArrayNames.StentPlanningDiameters);

    let outTuple1 = [];
    pointData.getTuple(dIdx, outTuple1);

    return {
        min: outTuple1[0],
        max: outTuple1[1],
        average: outTuple1[2],
        lumen: outTuple1[3]
    };
}

export function getLength(meshes, branch, fieldDataArrayNames, pointDataArrayNames, d1Idx, d2Idx) {
    var centerlineBranchSelection;
    if (branch === 'Left') {
        centerlineBranchSelection = CenterlineBranchSelection.DISTANCE_LEFT;
    } else if (branch === 'Right') {
        centerlineBranchSelection = CenterlineBranchSelection.DISTANCE_RIGHT;
    } else if (branch === 'Cusp') {
        centerlineBranchSelection = CenterlineBranchSelection.DISTANCE_CUSP;
    }

    const filter =
        makeRootFilter(
            CenterlineType.WALL,
            centerlineBranchSelection,
            meshes,
            fieldDataArrayNames);
    const pointData =
        filter.getOutputData(0)
            .getPointData()
            .getArrayByName(pointDataArrayNames.Distances);

    let outTuple1 = [];
    pointData.getTuple(d1Idx, outTuple1);
    let outTuple2 = [];
    pointData.getTuple(d2Idx, outTuple2);

    return Math.abs(outTuple1[0] - outTuple2[0]);
}

function getAzimuthDisplayAngle(angle) {
    if (angle >= 0.0) {
        return { angle: Math.abs(angle), label: "RAO" };
    } else if (angle < 0.0) {
        return { angle: Math.abs(angle), label: "LAO" };
    } else {
        return { angle: angle, label: "?" };
    }
}

function getElevationDisplayAngle(angle) {
    if (angle >= 0.0) {
        return { angle: angle, label: "CRA" };
    } else if (angle < 0.0) {
        return { angle: Math.abs(angle), label: "CAU" };
    } else {
        return { angle: angle, label: "?" };
    }
}

export function getBaselineMeasurements(meshes) {
    log("Getting baseline measurements");
    const centerlineData = meshes.wallCenterline;
    const lumenCenterlineData = meshes.lumenCenterline;
    const pointData = centerlineData.dataSource.getPointData();
    const wallFieldData = centerlineData.dataSource.getFieldData();
    const lumenFieldData = lumenCenterlineData.dataSource.getFieldData();

    const diameters = pointData.getArrayByName("Stent_Planning_Diameters");
    const circleEquivalents = pointData.getArrayByName("Equivalent_Circle_Diameter");
    const landmarkIds = centerlineData.dataSource.getFieldData().getArrayByName(constants.dataArrayNames.landmarkIds);
    const landmarkLengths = centerlineData.dataSource.getFieldData().getArrayByName(constants.dataArrayNames.landmarkLengths);
    const landmarkAngles = centerlineData.dataSource.getFieldData().getArrayByName(constants.dataArrayNames.landmarkAngles);
    const cArmAngles = centerlineData.dataSource.getFieldData().getArrayByName(constants.dataArrayNames.cArmAngles);
    const cArmAngleNames = centerlineData.dataSource.getFieldData().getArrayByName(constants.dataArrayNames.cArmAngleNames);

    const baselineMeasurements = [];

    for (var measName in measurementLabelInfo) {

        const labelInfo = measurementLabelInfo[measName];
        const index = labelInfo.landmarkIndex;
        const fieldData = wallFieldData;
        log(`Processing measurement ${measName}`);
        switch (labelInfo.labelType) {
            case LabelType.DIAMETER: {
                const masterIndex = landmarkIds.getTuple(index);
                const { branch: branch, index: transitionIndex } = convertMasterToTransitionBranchIndex(masterIndex, fieldData);
                const { branch: distanceBranch, index: distanceIndex } = convertMasterToDistanceBranchIndex(masterIndex, fieldData);
                const min = diameters.getComponent(masterIndex, 0);
                const max = diameters.getComponent(masterIndex, 1);
                const averageDiameter = diameters.getComponent(masterIndex, 2);
                const lumenDiameter = diameters.getComponent(masterIndex, 3);
                baselineMeasurements.push({
                    measurementName: measName,
                    measurementType: labelInfo.labelType,
                    branch: distanceBranch,
                    bounds: [labelInfo.measurementBounds],
                    transitionIndexes: [transitionIndex],
                    distanceIndexes: [distanceIndex],
                    measurements: [min, max, averageDiameter, lumenDiameter],
                    centerlineType: labelInfo?.centerlineType || CenterlineType.WALL,
                    childMeasurements: {
                        firstPlaneChildren: labelInfo.firstPlaneChildren ?? [],
                        secondPlaneChildren: labelInfo.secondPlaneChildren ?? [],
                        thirdPlaneChildren: [],
                        midpointBounds: []
                    },
                });
                break;
            }
            case LabelType.OFFSET_DIAMETER: {
                const masterIndex = landmarkIds.getTuple(index);
                const { branch: branch, index: transitionIndex } = convertMasterToTransitionBranchIndex(masterIndex, fieldData);
                const { branch: distanceBranch, index: distanceIndex } = convertMasterToDistanceBranchIndex(masterIndex, fieldData);
                const min = diameters.getComponent(masterIndex, 0);
                const max = diameters.getComponent(masterIndex, 1);
                const averageDiameter = diameters.getComponent(masterIndex, 2);
                const lumenDiameter = diameters.getComponent(masterIndex, 3);
                baselineMeasurements.push({
                    measurementName: measName,
                    measurementType: labelInfo.labelType,
                    branch: distanceBranch,
                    bounds: [labelInfo.measurementBounds],
                    transitionIndexes: [transitionIndex],
                    distanceIndexes: [distanceIndex],
                    measurements: [min, max, averageDiameter, lumenDiameter, labelInfo.offsetAmount],
                    centerlineType: labelInfo?.centerlineType || CenterlineType.WALL,
                    childMeasurements: {
                        firstPlaneChildren: [],
                        secondPlaneChildren: [],
                        thirdPlaneChildren: [],
                        midpointBounds: [],
                        offsetMeasurementName: labelInfo.offsetMeasurementName
                    },
                });
                break;
            }
            case LabelType.MIDPOINT_DIAMETER: {
                const masterIndex = landmarkIds.getTuple(index);
                const { branch: branch, index: transitionIndex } = convertMasterToTransitionBranchIndex(masterIndex, fieldData);
                const { branch: distanceBranch, index: distanceIndex } = convertMasterToDistanceBranchIndex(masterIndex, fieldData);
                const min = diameters.getComponent(masterIndex, 0);
                const max = diameters.getComponent(masterIndex, 1);
                const averageDiameter = diameters.getComponent(masterIndex, 2);
                const lumenDiameter = diameters.getComponent(masterIndex, 3);
                baselineMeasurements.push({
                    measurementName: measName,
                    measurementType: labelInfo.labelType,
                    branch: distanceBranch,
                    bounds: [labelInfo.measurementBounds],
                    transitionIndexes: [transitionIndex],
                    distanceIndexes: [distanceIndex],
                    measurements: [min, max, averageDiameter, lumenDiameter],
                    centerlineType: labelInfo?.centerlineType || CenterlineType.WALL,
                    childMeasurements: {
                        firstPlaneChildren: [],
                        secondPlaneChildren: [],
                        thirdPlaneChildren: [],
                        midpointBounds: labelInfo.midpointBounds ?? []
                    },
                });
                break;
            }
            case LabelType.LENGTH: {
                const baselineLength = landmarkLengths.getTuple(index);
                const startLabel = measurementLabelInfo[labelInfo.firstPlaneChildren[0]];
                const endLabel = measurementLabelInfo[labelInfo.secondPlaneChildren[0]];
                const startIndex = startLabel.landmarkIndex;
                const endIndex = endLabel.landmarkIndex;

                const startMasterIndex = landmarkIds.getTuple(startIndex);
                const endMasterIndex = landmarkIds.getTuple(endIndex);

                const { branch: _branchStart, index: transitionIndexStart } = convertMasterToTransitionBranchIndex(startMasterIndex, fieldData);
                const { branch: _branchEnd, index: transitionIndexEnd } = convertMasterToTransitionBranchIndex(endMasterIndex, fieldData);
                const { branch: _distanceBranchStart, index: distanceIndexStart } = convertMasterToDistanceBranchIndex(startMasterIndex, fieldData);
                const { branch: distanceBranchEnd, index: distanceIndexEnd } = convertMasterToDistanceBranchIndex(endMasterIndex, fieldData);

                baselineMeasurements.push({
                    measurementName: measName,
                    measurementType: labelInfo.labelType,
                    branch: distanceBranchEnd,
                    bounds: labelInfo.measurementBounds,
                    transitionIndexes: [transitionIndexStart, transitionIndexEnd],
                    distanceIndexes: [distanceIndexStart, distanceIndexEnd],
                    measurements: [baselineLength],
                    centerlineType: startLabel?.centerlineType || CenterlineType.WALL,
                    bottomCenterlineType: endLabel?.centerlineType || CenterlineType.WALL,
                    childMeasurements: {
                        firstPlaneChildren: labelInfo.firstPlaneChildren ?? [],
                        secondPlaneChildren: labelInfo.secondPlaneChildren ?? [],
                        thirdPlaneChildren: [],
                        midpointBounds: []
                    },
                });
                break;
            }
            case LabelType.CARM: {
                const angles = cArmAngles.getTuples(labelInfo.landmarkIndex, labelInfo.landmarkIndex + 1);

                baselineMeasurements.push({
                    measurementName: measName,
                    measurementType: labelInfo.labelType,
                    measurements: angles,
                    childMeasurements: {
                        firstPlaneChildren: [labelInfo.measurementFocus],
                    }
                });
                break;
            }
            case LabelType.ANGLE:
            default:
                console.error(`Unable to generate baseline measurements for unknown measurement type ${labelInfo.labelType} for measurement ${measName} `);
        }
    }
    debug(() => global.debugBaselines = baselineMeasurements);

    return baselineMeasurements;
}

const computePoints = (label, screenCoords, isRightSideLabel, scale) => {
    const x = isRightSideLabel ? label.x() - 5 : label.x() + (label.getText().width() * scale) + 5;
    return [x, label.y() + (label.height() / 2), screenCoords.x, screenCoords.y];
};

const mkLine = (measurementName, label, screenCoords, isRightSideLabel, labelColor, scale) => {
    const points = computePoints(label, screenCoords, isRightSideLabel, scale);
    return new Konva.Line({
        points: points.some(isNaN) ? [0, 0, 0, 0] : points,
        stroke: labelColor,
        strokeWidth: 2,
        lineCap: 'round',
        lineJoin: 'round',
        name: `${measurementName}-line`
    });
};

function mkLabels(stage, parentContext, stentPlanningProps, measurementName, labelInformation, valueText, isInteractive, isIndexInBounds, isParent) {
    let visibilityLabel = null;
    let visibilityText = null;

    if (isParent) {
        visibilityLabel = new Konva.Label({
            x: stage.width() * labelInformation.xOffset,
            y: stage.height() * labelInformation.yOffset,
            name: `${measurementName}-visibilityLabel`
        });

        const visibilityTag = new Konva.Tag({
            fill: labelInformation.labelColor,
            cornerRadius: [5, 0, 0, 5],
            stroke: labelInformation.labelColor,
            strokeWidth: 1
        });

        visibilityText = new Konva.Text({
            text: labelCollapsedIcon,
            fontSize: labelFontSize,
            fontFamily: labelIconFontFamily,
            fontStyle: labelIconFontStyle,
            fill: 'white',
            padding: labelPadding
        });

        visibilityLabel.add(visibilityTag);
        visibilityLabel.add(visibilityText);

        visibilityLabel.xOffset = labelInformation.xOffset;
        visibilityLabel.yOffset = labelInformation.yOffset;
    }

    const parentVisibilityLabel = !isParent ? stage.findOne(`.${labelInformation.childGroup}-visibilityLabel`) : null;

    const stentY = !isNullOrUndefined(parentVisibilityLabel) ?
        parentVisibilityLabel.y() + (parentVisibilityLabel.height() * labelInformation.childYOffset)
        : stage.height() * labelInformation.yOffset;

    const stentX = !isNullOrUndefined(visibilityLabel) ?
        visibilityLabel.x() * visibilityText.width()
        : stage.width() * labelInformation.xOffset;

    const stentLabel = new Konva.Label({
        x: stentX,
        y: stentY,
        name: `${measurementName}-stentLabel`
    });

    const stentTag = new Konva.Tag({
        fill: labelInformation.labelColor,
        cornerRadius: isNullOrUndefined(visibilityLabel) ? [5, 0, 0, 5] : [0, 0, 0, 0],
        stroke: labelInformation.labelColor,
        strokeWidth: 1,
        name: `${measurementName}-stentTag`
    });

    const stentText = new Konva.Text({
        text: labelInformation.labelName,
        fontSize: labelFontSize,
        fontFamily: labelTextFontFamily,
        fill: 'white',
        padding: labelPadding,
        name: `${measurementName}-stentText`
    });

    stentLabel.add(stentTag);
    stentLabel.add(stentText);

    stentLabel.xOffset = labelInformation.xOffset;
    stentLabel.yOffset = stentY;

    const valueLabel = new Konva.Label({
        x: stentLabel.x() + stentText.width(),
        y: stentY,
        name: `${measurementName}-valueLabel`
    });

    const valueTag = new Konva.Tag({
        fill: 'white',
        cornerRadius: labelInformation?.canBeDisabled ? [0, 0, 0, 0] : [0, 5, 5, 0],
        stroke: labelInformation.labelColor,
        strokeWidth: 1,
        name: `${measurementName}-valueTag`
    });

    const whiteText = new Konva.Text({
        text: valueText,
        fontSize: labelFontSize,
        fontFamily: labelTextFontFamily,
        fill: 'black',
        padding: labelPadding,
        name: `${measurementName}-valueText`
    });

    valueLabel.add(valueTag);
    valueLabel.add(whiteText);

    valueLabel.yOffset = stentY;

    let disableLabel = null;
    if (labelInformation?.canBeDisabled) {
        disableLabel = new Konva.Label({
            x: valueLabel.x() + valueLabel.width(),
            y: stentY,
            name: `${measurementName}-disableLabel`,
            clicked: false
        });

        const disableTag = new Konva.Tag({
            fill: labelInformation.labelColor,
            cornerRadius: [0, 5, 5, 0],
            stroke: labelInformation.labelColor,
            strokeWidth: 1,
            name: `${measurementName}-disableTag`
        })

        const disableText = new Konva.Text({
            text: labelEnabledIcon,
            fontSize: labelFontSize,
            fontFamily: labelIconFontFamily,
            fontStyle: labelIconFontStyle,
            fill: 'white',
            padding: labelPadding,
            name: `${measurementName}-disableText`
        })

        disableLabel.add(disableTag);
        disableLabel.add(disableText);

        disableLabel.yOffset = stentY;
    }

    const mkInteractive = (label) => {
        label.on('mousedown', () => {
            stentPlanningProps.onMeasurementLabelClicked(measurementName);
        });
        label.on('mouseover', () => {
            document.body.style.cursor = 'pointer';
        });
        label.on('mouseout', () => {
            document.body.style.cursor = 'default';
        });
    };

    if (isInteractive && isIndexInBounds) {
        mkInteractive(stentLabel);
        mkInteractive(valueLabel);
    }

    return { visibilityLabel, stentLabel, valueLabel, disableLabel };
}

function addLabelAndChildren(parentContext, context, stentPlanningProps, measurementName, layer, labelInfo, valueText, isInteractive, isIndexInBounds) {
    if (context.current?.stage && stentPlanningProps && context.current.konvaElements && layer && labelInfo) {
        log("Adding label: " + labelInfo.labelName);
        const isParent = !isNullOrUndefined(labelInfo.parentGroup) && labelInfo.parentGroup.trim().length !== 0;

        const { visibilityLabel, stentLabel, valueLabel, disableLabel } =
            mkLabels(
                context.current.stage,
                parentContext,
                stentPlanningProps,
                measurementName,
                labelInfo,
                valueText,
                isInteractive,
                isIndexInBounds,
                isParent
            );

        let group = null;

        if (disableLabel) {
            disableLabel.on('mousedown', () => {
                disableLabel.setAttr('clicked', !disableLabel.getAttr('clicked'));
                disableLabel.getText().text(disableLabel.getAttr('clicked') ? labelDisabledIcon : labelEnabledIcon);
                layer.draw();
                parentContext.renderer.getRenderWindow().render();
            })
        }

        if (isParent) {
            const findGroup = layer.findOne(`#${labelInfo.parentGroup}`);

            if (!isNullOrUndefined(findGroup)) {
                group = findGroup;
            } else {
                group = new Konva.Group({ id: labelInfo.parentGroup, visible: false, clicked: false });
                layer.add(group);
            }

            stentLabel.on('mouseover', () => {
                group.visible(true);
                document.body.style.cursor = 'pointer';
                layer.draw();
            });

            stentLabel.on('mouseout', () => {
                if (!group.getAttr('clicked')) { group.visible(false); }
                document.body.style.cursor = 'default';
                layer.draw();
            });

            valueLabel.on('mouseover', () => {
                group.visible(true);
                document.body.style.cursor = 'pointer';
                layer.draw();
            });

            valueLabel.on('mouseout', () => {
                if (!group.getAttr('clicked')) { group.visible(false); }
                document.body.style.cursor = 'default';
                layer.draw();
            });

            visibilityLabel.on('mousedown', () => {
                group.setAttr('clicked', !group.getAttr('clicked'));
                group.visible(group.getAttr('clicked'));
                visibilityLabel.getText().text(group.getAttr('clicked') ? labelExpandedIcon : labelCollapsedIcon);
                layer.draw();
                parentContext.renderer.getRenderWindow().render();
            });

            layer.add(stentLabel);
            layer.add(valueLabel);
            layer.add(visibilityLabel);
            if (disableLabel) { layer.add(disableLabel) }
        } else if (!isNullOrUndefined(labelInfo.childGroup)) {
            const group = layer.findOne(`#${labelInfo.childGroup}`);
            group.add(stentLabel);
            group.add(valueLabel);
            if (disableLabel) { group.add(disableLabel) }
        } else {
            layer.add(stentLabel);
            layer.add(valueLabel);
            if (disableLabel) { layer.add(disableLabel) }
        }
        layer.draw();
        return { visibilityLabel, stentLabel, valueLabel, disableLabel, group };
    }
}

/**
 *
 * @param {Measurement} measurement
 * @param {any} displayMeasurementType
 * @returns {float}
 */
function getDisplayMeasurement(measurement, displayMeasurementType) {
    switch (displayMeasurementType) {
        case DisplayMeasurementType.MIN: {
            return measurement.stentPlanningDiameters.min;
        }
        case DisplayMeasurementType.MAX: {
            return measurement.stentPlanningDiameters.max;
        }
        case DisplayMeasurementType.LUMEN: {
            return measurement.stentPlanningDiameters.lumen;
        }
        case DisplayMeasurementType.AVERAGE:
        default:
            return measurement.stentPlanningDiameters.average;
    }
}

const updateLabelStyle = (label, isValueLabel, isActive, labelColor, isDisabled) => {
    const tag = label.findOne('Tag');
    if (isDisabled) {
        tag?.fill('grey');
        tag?.stroke('grey');
        return;
    }
    if (!isValueLabel) {
        tag?.fill(isActive ? 'red' : labelColor);
    } else {
        tag?.fill('white');
    }
    tag?.stroke(isActive ? 'red' : labelColor);
};

const updateLineStyle = (line, isActive, labelColor) => {
    line?.stroke(isActive ? 'red' : labelColor);
};

const getBranchTypeFromName = (branchName) => {
    const mapping = {
        [CenterlineDistanceBranchSelection.LEFT]: CenterlineBranchSelection.DISTANCE_LEFT,
        [CenterlineDistanceBranchSelection.CUSP]: CenterlineBranchSelection.DISTANCE_CUSP,
        [CenterlineDistanceBranchSelection.RIGHT]: CenterlineBranchSelection.DISTANCE_RIGHT
    };
    return mapping[branchName] || null;
};

const updateRingStyle = (index, ring, isActive, activeRings) => {
    if (activeRings.includes(index)) { return; }
    if (isActive) {
        activeRings.push(index);
        ring?.actor.getProperty().setColor(1, 0, 0);
    } else {
        ring?.actor.getProperty().setColor(0, 0, 1);
    }
};

/**
 * Updates the diameter and color of the rings based on the provided arguments.
 *
 * @param {UpdateRingArgs} updateRingArgs - The arguments needed to update the diameter rings.
 */
function updateRings({
    group,
    measurementName,
    unifiedBranchInfo,
    branchType,
    meshes,
    labelInfo,
    parentContext,
    labelLine,
    isActive,
    isDisabled,
    activeRings,
    skipAddRings = false
}) {
    const isParentOrSingle = isNullOrUndefined(labelInfo?.childGroup);
    const isChildVisible = group && group.visible();
    if (isDisabled) { return;}
    if ((isChildVisible || isParentOrSingle) && unifiedBranchInfo) {
        if (measurementName.includes('Diameter') && !isNullOrUndefined(unifiedBranchInfo[0]?.index)) {
            const index = convertUnifiedBranchIndexToMasterIndex(unifiedBranchInfo[0].index, branchType, meshes.wallCenterline.dataSource.getFieldData());
            const ring = meshes.diameterRings.get(index);

            if (ring) {
                log(ring);
                const points = ring.dataSource?.getPoints();
                if (!points) {
                    console.warn(`Diameter ring at index ${index} has no points object`);
                    return;
                }

                log(points);
                const ringPoints = points.getTuples();
                if (!ringPoints || ringPoints.length === 0) {
                    console.warn(`Diameter ring at index ${index} has no valid point data`);
                    return;
                }

                const chunk = points.getNumberOfComponents();
                let closestPointCoords = [];
                let minX = Infinity;
                let maxX = -Infinity;
                for (let i = 0; i < ringPoints.length; i += chunk) {
                    const x = ringPoints[i];
                    const y = ringPoints[i + 1];
                    const z = ringPoints[i + 2];

                    if (labelInfo.xOffset >= rightSideXOffset) {
                        if (x > maxX) {
                            closestPointCoords = [x, y, z];
                            maxX = x;
                        }
                    } else {
                        if (x < minX) {
                            closestPointCoords = [x, y, z];
                            minX = x;
                        }
                    }
                }

                const closestPoint = computeScreenCoordinates(parentContext.renderer, closestPointCoords);
                if (labelLine) { labelLine.points(labelLine.points().slice(0, 2).concat([closestPoint.x || 0, closestPoint.y || 0])); }
                if (!skipAddRings) {
                    parentContext.renderer.addActor(ring.actor);
                }
                updateRingStyle(index, ring, isActive, activeRings);
            }
        }
        else if (measurementName.includes('Length') && !isNullOrUndefined(unifiedBranchInfo[0]?.firstIndex) && !isNullOrUndefined(unifiedBranchInfo[0]?.secondIndex)) {
            const firstIndex = convertUnifiedBranchIndexToMasterIndex(unifiedBranchInfo[0].firstIndex, branchType, meshes.wallCenterline.dataSource.getFieldData());
            const secondIndex = convertUnifiedBranchIndexToMasterIndex(unifiedBranchInfo[0].secondIndex, branchType, meshes.wallCenterline.dataSource.getFieldData());
            const firstRing = meshes.diameterRings.get(firstIndex);
            const secondRing = meshes.diameterRings.get(secondIndex);
            if (firstRing) { updateRingStyle(firstIndex, firstRing, isActive, activeRings); }
            if (secondRing) { updateRingStyle(secondIndex, secondRing, isActive, activeRings); }
        }
    }
}

function updateLabelLayer(context, stentPlanningProps, meshes, parentContext, scale = (window.devicePixelRatio), skipAddRings = false) {
    const mkInteractive = (label, measurementName, group, isParent, isVisibilityLabel, updateRingArgs) => {
        const layer = context.current.layer;

        // Activate measurement on click.
        label.on('mousedown', () => {
            if (!isVisibilityLabel) { stentPlanningProps.onMeasurementLabelClicked(measurementName); }
            if (isParent && !isNullOrUndefined(group)) {
                const newVisibility = isVisibilityLabel ? !group.getAttr('clicked') : true;
                group.setAttr('clicked', newVisibility);
                group.visible(group.getAttr('clicked'));
                if (isVisibilityLabel) { label.getText().text(group.getAttr('clicked') ? labelExpandedIcon : labelCollapsedIcon); }
                updateRings({ ...updateRingArgs, group: group });
                parentContext.renderer.getRenderWindow().render();
                layer.draw();
            }
        });

        // Display children on hover.
        label.on('mouseover', () => {
            document.body.style.cursor = 'pointer';
            if (isParent && !isNullOrUndefined(group)) {
                group.visible(true);
                layer.draw();
            }
        });

        // Hide children when not hovered.
        label.on('mouseout', () => {
            document.body.style.cursor = 'default';
            if (isParent && !isNullOrUndefined(group)) {
                if (!group.getAttr('clicked')) { group.visible(false); }
                layer.draw();
            }
        });
    };

    const removeInteractive = (label) => {
        label.off('mousedown');
        label.off('mouseover');
        label.off('mouseout');
    };

    /**
     * Updates the event listeners for the stent labels.
     * MouseDown, Over, and Out listeners will be removed and then re-added if the label is interactive.
     *
     * @param {Label} label - The Konva Label to update.
     * @param {string} measurementName - The name of measurement the label is tied to. Needed to pass along to callback functions.
     * @param {bool} isInteractive - Flag to specify if the label is interactive or not.
     * @param {Group} group - The Konva group the label belongs to, if any.
     * @param {bool} isParent - Flag to specify if the label is a parent label (has children) or not. Used to toggle group visibility on click
     * @param {bool} isDisabled - Flag to specify if the label has been disabled. If the label is disabled, interactivity will be removed.
     * @param {bool} isVisibilityLabel - Flag to specify if the label is a visibility label. Group visibility toggle is only enabled on visibility labels.
     * @param {UpdateRingArgs} updateRingArgs - The arguments needed to update the diameter rings.
     */
    const updateInteractive = (label, measurementName, isInteractive, group, isParent, isVisibilityLabel, isDisabled, updateRingArgs) => {
        if (isInteractive) {
            removeInteractive(label);
            if (!isDisabled) { mkInteractive(label, measurementName, group, isParent, isVisibilityLabel, updateRingArgs) };
        } else {
            removeInteractive(label);
        }
    };

    if (context.current.stage && context.current.layer && stentPlanningProps && !isNullOrUndefined(context.current.konvaElements)) {
        const activeRings = [];

        stentPlanningProps.currentMeasurements.forEach((measurement) => {
            //Unpacking the StentMeasurement DU
            //The DU is made up of 2 components:
            // fields[0] - MeasurementInfo {MeasurementName, RelatedMeasurementNames, MeasurementBounds}
            // fields[1] - The unified branch measurement
            const measurementName = measurement.fields[0].MeasurementName;
            const measurementBounds = measurement.fields[0].MeasurementBounds;
            const labelInfo = measurementLabelInfo[measurementName];
            const unifiedBranchInfo = measurement.fields[1];
            const isActive = stentPlanningProps.maybeActiveMeasurement?.fields[0].MeasurementName === measurementName;

            if (getDUName(measurement) === 'CArmAngle') {
                const { stentLabel, valueLabel } = context.current.konvaElements[measurementName];
                if (stentLabel) {
                    stentLabel.x(context.current.stage.width() * labelInfo.xOffset);
                    stentLabel.y(context.current.stage.height() * labelInfo.yOffset);
                    stentLabel.scale({ x: scale, y: scale });
                    updateLabelStyle(stentLabel, false, isActive, labelInfo.labelColor, false);
                }

                if (valueLabel) {
                    const azimuthAngle = unifiedBranchInfo?.FirstAngle;
                    const elevationAngle = unifiedBranchInfo?.SecondAngle;

                    const displayAzimuth = getAzimuthDisplayAngle(azimuthAngle);
                    const displayElevation = getElevationDisplayAngle(elevationAngle);

                    const updatedValueText = `${displayAzimuth.angle?.toFixed(0)}° ${displayAzimuth.label}, ${displayElevation.angle?.toFixed(0)}° ${displayElevation.label}`;
                    if (!isNullOrUndefined(updatedValueText)) { valueLabel.getText().text(updatedValueText); }
                    valueLabel.x(stentLabel.x() + (stentLabel.getText().width() * scale));
                    valueLabel.y(context.current.stage.height() * labelInfo.yOffset);
                    valueLabel.scale({ x: scale, y: scale });
                    updateLabelStyle(valueLabel, true, isActive, labelInfo.labelColor, false);
                }

                parentContext.renderer.getRenderWindow().render();
                context.current.konvaElements[measurementName] = { stentLabel: stentLabel, valueLabel: valueLabel, labelLine: null, visibilityLabel: null };
                return;
            }

            const measurements = unifiedBranchInfo[1];
            const branchInfo = unifiedBranchInfo[0];
            const branchName = branchInfo.branch.name;
            let branchType = null;

            const isIndexValid =
                getDUName(measurement).includes('Diameter')
                    ? indexInBounds(unifiedBranchInfo[0].index, measurementBounds, meshes.wallCenterline.dataSource.getFieldData())
                    : true;

            if (branchName === CenterlineDistanceBranchSelection.LEFT) {
                branchType = CenterlineBranchSelection.DISTANCE_LEFT;
            } else if (branchName === CenterlineDistanceBranchSelection.CUSP) {
                branchType = CenterlineBranchSelection.DISTANCE_CUSP;
            } else if (branchName === CenterlineDistanceBranchSelection.RIGHT) {
                branchType = CenterlineBranchSelection.DISTANCE_RIGHT;
            }

            const labelElements = context.current.konvaElements[measurementName];
            const { stentLabel, valueLabel, visibilityLabel, disableLabel } = labelElements;
            let { labelLine } = labelElements;

            const parentVisibilityLabel =
                !isParent
                    ? context.current.layer.findOne(`.${labelInfo.childGroup}-visibilityLabel`)
                    : null;

            const stentY =
                !isNullOrUndefined(parentVisibilityLabel)
                    ? parentVisibilityLabel.y() + ((parentVisibilityLabel.height() * scale) * labelInfo.childYOffset)
                    : context.current.stage.height() * labelInfo.yOffset;

            const group =
                context.current.layer.findOne(`#${labelInfo?.parentGroup}`) ||
                context.current.layer.findOne(`#${labelInfo?.childGroup}`);

            const isParent = !isNullOrUndefined(labelInfo?.parentGroup);
            let isDisabled = disableLabel?.getAttr('clicked') || false;
            let valueText = "--";
            if (isDisabled) {
                valueText = "--";
            } else if (getDUName(measurement).includes('Diameter')) {
                const displayMeasurement = getDisplayMeasurement(measurements, labelInfo?.displayMeasurementType);
                valueText = isIndexValid ? displayMeasurement.toFixed(1) : "--";
            } else if (getDUName(measurement) === 'Length') {
                valueText = isIndexValid ? measurements.toFixed(1) : "--";
            }

            const updateRingArgs = {
                group,
                measurementName: getDUName(measurement),
                unifiedBranchInfo,
                branchType,
                isIndexValid,
                meshes,
                labelInfo,
                parentContext,
                labelLine,
                isActive,
                isDisabled,
                activeRings,
                skipAddRings
            };

            if (visibilityLabel) {
                visibilityLabel.x(context.current.stage.width() * labelInfo.xOffset);
                visibilityLabel.y(context.current.stage.height() * labelInfo.yOffset);
                visibilityLabel.scale({ x: scale, y: scale });
                visibilityLabel.getText().text(group?.visible() ? labelExpandedIcon : labelCollapsedIcon); //u+25BC is the unicode for "⌄" u+25B6 is the unicode for "˃"

                updateInteractive(
                    visibilityLabel,
                    measurementName,
                    isIndexValid,
                    group,
                    isParent,
                    true,
                    isDisabled,
                    updateRingArgs
                );

                updateLabelStyle(visibilityLabel, false, isActive, labelInfo.labelColor, isDisabled);
            }

            if (stentLabel) {
                if (visibilityLabel) {
                    stentLabel.x(visibilityLabel.x() + (visibilityLabel.getText().width() * scale));
                } else {
                    stentLabel.x(context.current.stage.width() * labelInfo.xOffset);
                }
                stentLabel.y(stentY);
                stentLabel.scale({ x: scale, y: scale });
                updateInteractive(stentLabel, measurementName, isIndexValid, group, isParent, false, isDisabled, updateRingArgs);
                updateLabelStyle(stentLabel, false, isActive, labelInfo.labelColor, isDisabled);
            }

            if (valueLabel) {
                valueLabel.getText().text(valueText);
                valueLabel.x(stentLabel.x() + (stentLabel.getText().width() * scale));
                valueLabel.y(stentY);
                valueLabel.scale({ x: scale, y: scale });
                updateInteractive(valueLabel, measurementName, isIndexValid, group, isParent, false, isDisabled, updateRingArgs);
                updateLabelStyle(valueLabel, true, isActive, labelInfo.labelColor, isDisabled);
            }

            if (disableLabel) {
                disableLabel.off('mousedown');
                disableLabel.off('mouseover');
                disableLabel.off('mouseout');

                disableLabel.on('mousedown', () => {
                    disableLabel.setAttr('clicked', !disableLabel.getAttr('clicked'));
                    disableLabel.getText().text(disableLabel.getAttr('clicked') ? labelDisabledIcon : labelEnabledIcon);
                    context.current.layer.draw();
                    parentContext.renderer.getRenderWindow().render();
                })
                disableLabel.on('mouseover', () => {
                    document.body.style.cursor = 'pointer';
                });

                disableLabel.on('mouseout', () => {
                    document.body.style.cursor = 'default';
                });
                disableLabel.x(valueLabel.x() + (valueLabel.getText().width() * scale));
                disableLabel.y(stentY);
                disableLabel.scale({ x: scale, y: scale });
                disableLabel.getText().setText(disableLabel.getAttr('clicked') ? labelDisabledIcon : labelEnabledIcon);
                updateLabelStyle(disableLabel, false, isActive, labelInfo.labelColor, isDisabled);
            }

            const filter =
                makeRootFilter(
                    CenterlineType.WALL,
                    branchType,
                    meshes,
                    stentPlanningProps.fieldDataArrayNames
                );

            //Measurement has no label line but the index is valid
            if (!labelLine && isIndexValid && !isDisabled) {
                const screenCoords =
                    getCenterlineCoords(filter, unifiedBranchInfo, parentContext.renderer);

                const [targetLabel, isRightSideLabel] =
                    (labelInfo.xOffset >= rightSideXOffset)
                        ? [stentLabel, true]
                        : [disableLabel || valueLabel, false];

                const line =
                    mkLine(measurementName, targetLabel, screenCoords, isRightSideLabel, labelInfo.labelColor, scale);

                line.strokeWidth(Math.max(2, 2 * (1 / window.devicePixelRatio)));
                log(line.points());

                if (!isNullOrUndefined(group) && !isParent) {
                    group.add(line);
                } else {
                    context.current.layer.add(line);
                }
                labelLine = line;
                //Measurement has a label line but the index is invalid OR label isDisabled
            } else if ((labelLine && !isIndexValid) || isDisabled) {
                labelLine?.destroy();
                labelLine = null;
            }

            //Measurement has a label line and the index is valid
            if (valueLabel && (visibilityLabel || stentLabel) && labelLine && unifiedBranchInfo && isIndexValid && !isDisabled) {
                const leftSideLabel = visibilityLabel || stentLabel;

                const screenCoords =
                    getCenterlineCoords(filter, unifiedBranchInfo, parentContext.renderer);

                const [targetLabel, isRightSideLabel] =
                    (labelInfo.xOffset >= rightSideXOffset)
                        ? [leftSideLabel, true]
                        : [disableLabel || valueLabel, false];

                const updatedComputedPoints = computePoints(targetLabel, screenCoords, isRightSideLabel, scale);

                if (!updatedComputedPoints.some(isNaN)) {
                    labelLine.points(updatedComputedPoints);
                    //minimum stroke width of 2, scaling up as we zoom out
                    const strokeWidth = Math.max(2, 2 * scale);
                    labelLine.strokeWidth(strokeWidth);
                }
                updateLineStyle(labelLine, isActive, labelInfo.labelColor);
            } else if (labelLine && isDisabled) {
                labelLine?.destroy();
                labelLine = null;
            }


            updateRings({
                group,
                measurementName: getDUName(measurement),
                unifiedBranchInfo,
                branchType,
                meshes,
                labelInfo,
                parentContext,
                labelLine,
                isActive,
                isDisabled,
                activeRings,
                skipAddRings
            });

            parentContext.renderer.getRenderWindow().render();

            context.current.konvaElements[measurementName] = {
                stentLabel: stentLabel,
                valueLabel: valueLabel,
                labelLine: labelLine,
                visibilityLabel: visibilityLabel,
                disableLabel: disableLabel
            };
        });
    }
}

export function useStentPlanningLabels(container, parentContext, meshes, stentPlanningProps) {
    const context = useRef(null);
    const parentContextRef = useRef(null);
    const meshesRef = useRef(null);
    const stentPlanningPropsRef = useRef(null);

    useEffect(() => {
        parentContextRef.current = parentContext;
    }, [parentContext]);

    useEffect(() => {
        meshesRef.current = meshes;
    }, [meshes]);

    useEffect(() => {
        stentPlanningPropsRef.current = stentPlanningProps;
    }, [stentPlanningProps]);

    function setUpScreenshotHandler() {
        // Helper function to toggle label visibility based on label types.
        function toggleLabelsByType(layer, konvaElements, labelTypesToHide, labelTypesToShow) {
            // Hide specified label types.
            for (const measurementName in measurementLabelInfo) {
                const labelInfo = measurementLabelInfo[measurementName];
                const labelType = labelInfo.labelType;
                const elements = konvaElements[measurementName];
                if (elements && (elements.disableLabel?.getAttr('clicked') || labelTypesToHide.includes(labelType))) {
                    elements.stentLabel?.visible(false);
                    elements.valueLabel?.visible(false);
                    elements.labelLine?.visible(false);
                    elements.visibilityLabel?.visible(false);
                    elements.disableLabel?.visible(false);
                }
            }

            // Show specified label types.
            for (const measurementName in measurementLabelInfo) {
                const labelInfo = measurementLabelInfo[measurementName];
                const labelType = labelInfo.labelType;

                if (labelTypesToShow.includes(labelType)) {
                    const elements = konvaElements[measurementName];
                    const isDisabled = elements.disableLabel?.getAttr('clicked');
                    if (elements) {
                        elements.stentLabel?.visible(!isDisabled);
                        elements.valueLabel?.visible(!isDisabled);
                        elements.labelLine?.visible(!isDisabled);
                        elements.visibilityLabel?.visible(!isDisabled);
                        //always hide the disable label
                        elements.disableLabel?.visible(false);
                    }
                }
            }
        }

        function toggleLabelsByGroup(layer, konvaElements, labelGroupsToShow) {
            // Show specified label types.
            for (const measurementName in measurementLabelInfo) {
                const labelInfo = measurementLabelInfo[measurementName];
                const labelGroup = labelInfo.parentGroup || labelInfo.childGroup || measurementName || "";
                const elements = konvaElements[measurementName];

                if (elements && labelGroupsToShow.includes(labelGroup) && !elements.disableLabel?.getAttr('clicked')) {
                    elements.stentLabel?.visible(true);
                    elements.valueLabel?.visible(true);
                    elements.labelLine?.visible(true);
                    elements.visibilityLabel?.visible(true);
                    //always hide the disable label
                    elements.disableLabel?.visible(false);
                } else {
                    if (elements) {
                        elements.stentLabel?.visible(false);
                        elements.valueLabel?.visible(false);
                        elements.labelLine?.visible(false);
                        elements.visibilityLabel?.visible(false);
                        elements.disableLabel?.visible(false);
                    }
                }
            }
        }

        // Helper function to expand parent groups for shown label types.
        function expandParentGroups(layer, konvaElements, labelTypesToExpand) {
            for (const measurementName in measurementLabelInfo) {
                const labelInfo = measurementLabelInfo[measurementName];
                const labelType = labelInfo.labelType;
                const parentGroup = labelInfo.parentGroup;
                const elements = konvaElements[measurementName];

                if (labelTypesToExpand.includes(labelType) && parentGroup && !elements?.disableLabel?.getAttr('clicked')) {
                    const group = layer.findOne(`#${parentGroup}`);
                    if (group) {
                        const newVisibility = true;
                        group.setAttr('clicked', newVisibility);
                        group.visible(newVisibility);

                        // Update visibility indicator to show expanded state.
                        const visibilityLabel = layer.findOne(`.${parentGroup}-visibilityLabel`);
                        if (visibilityLabel) {
                            const visibilityText = visibilityLabel.getText();
                            if (visibilityText) {
                                visibilityText.text(labelExpandedIcon); // Down arrow indicating expanded.
                            }
                        }
                    }
                }
            }
        }

        // Helper function to update rings for measurements.
        function updateRingsForMeasurements(layer, konvaElements, activeRings, measurementFilter, activeMeasurements) {
            if (stentPlanningPropsRef.current?.currentMeasurements && meshesRef.current && parentContextRef.current) {
                stentPlanningPropsRef.current.currentMeasurements.forEach((measurement) => {
                    const measurementName = measurement.fields[0].MeasurementName;
                    const labelInfo = measurementLabelInfo[measurementName];
                    const unifiedBranchInfo = measurement.fields[1];

                    if (!measurementFilter(measurement, unifiedBranchInfo)) {
                        return;
                    }

                    const branchInfo = unifiedBranchInfo[0];
                    const branchName = branchInfo?.branch?.name;
                    const branchType = getBranchTypeFromName(branchName);

                    const group =
                        layer.findOne(`#${labelInfo?.parentGroup}`) ||
                        layer.findOne(`#${labelInfo?.childGroup}`);

                    const isActive = activeMeasurements && activeMeasurements.includes(measurementName);

                    const elements = konvaElements[measurementName];
                    const updateRingArgs = {
                        group,
                        measurementName: getDUName(measurement),
                        unifiedBranchInfo,
                        branchType,
                        meshes: meshesRef.current,
                        labelInfo,
                        parentContext: parentContextRef.current,
                        labelLine: elements?.labelLine,
                        isActive,
                        isDisabled: elements?.disableLabel?.getAttr('clicked'),
                        activeRings
                    };
                    updateRings(updateRingArgs);
                });

                parentContextRef.current.renderer.getRenderWindow().render();
            }
        }

        function handleDiameterScreenshot() {
            if (!context.current?.layer || !context.current?.konvaElements) {
                console.error("Cannot handle diameter screenshot: context not initialized");
                return;
            }

            const layer = context.current.layer;
            const konvaElements = context.current.konvaElements;

            // Hide C-ARM and Length labels, show diameter labels.
            const showTypes = [LabelType.DIAMETER, LabelType.OFFSET_DIAMETER, LabelType.MIDPOINT_DIAMETER];
            const hideTypes = [LabelType.CARM, LabelType.LENGTH];
            toggleLabelsByType(layer, konvaElements, hideTypes, showTypes);
            expandParentGroups(layer, konvaElements, showTypes);

            // Apply scaling to labels and connecting lines.
            updateLabelLayer(context, context.current.stentPlanningProps, meshes, parentContext, 1.6);

            // Update diameter rings for all visible diameter measurements.
            const activeRings = [];
            updateRingsForMeasurements(layer, konvaElements, activeRings, (measurement) => {
                return getDUName(measurement).includes('Diameter');
            });

            layer.draw();

            // Capture composite screenshot after all updates are complete.
            return captureCompositeScreenshot();
        }

        function handleLengthScreenshot() {
            if (!context.current?.layer || !context.current?.konvaElements) {
                console.error("Cannot handle length screenshot: context not initialized");
                return;
            }

            const layer = context.current.layer;
            const konvaElements = context.current.konvaElements;

            // Hide diameter labels, show length and C-ARM labels.
            const hideTypes = [LabelType.DIAMETER, LabelType.OFFSET_DIAMETER, LabelType.MIDPOINT_DIAMETER];
            const showTypes = [LabelType.LENGTH, LabelType.CARM];
            toggleLabelsByType(layer, konvaElements, hideTypes, showTypes);
            expandParentGroups(layer, konvaElements, showTypes);

            // Apply scaling to labels and connecting lines.
            updateLabelLayer(context, context.current.stentPlanningProps, meshes, parentContext, 1.6);

            // Hide all diameter rings first.
            if (meshesRef.current && parentContextRef.current) {
                meshesRef.current.diameterRings.forEach((diameterRing) => {
                    parentContextRef.current.renderer.removeActor(diameterRing.actor);
                });
            }

            // Show diameter rings for length measurement endpoints.
            // We need to find diameter measurements that match the length endpoints.
            if (stentPlanningPropsRef.current?.currentMeasurements && meshesRef.current && parentContextRef.current) {
                const lengthEndpointIndices = new Set();

                // Collect all indices from length measurements
                stentPlanningPropsRef.current.currentMeasurements.forEach((measurement) => {
                    const unifiedBranchInfo = measurement.fields[1];

                    if (getDUName(measurement) === 'Length' &&
                        !isNullOrUndefined(unifiedBranchInfo[0]?.firstIndex) &&
                        !isNullOrUndefined(unifiedBranchInfo[0]?.secondIndex)) {
                        lengthEndpointIndices.add(unifiedBranchInfo[0].firstIndex);
                        lengthEndpointIndices.add(unifiedBranchInfo[0].secondIndex);
                    }
                });

                // Now show rings for diameter measurements whose indices match length endpoints.
                // TODO: There may be a race condition here where the standard stent planning rendering logic can
                //  kick in and override our changes. This can happen if the user moves their mouse over a viewport
                //  (mitigated by placing a non-interactive div on top of the workspace), or if the tick event fires.
                //  The impact is that the rings may not be in the desired locations in the screenshot.
                stentPlanningPropsRef.current.currentMeasurements.forEach((measurement) => {
                    const unifiedBranchInfo = measurement.fields[1];

                    if (getDUName(measurement).includes('Diameter') && !isNullOrUndefined(unifiedBranchInfo[0]?.index)) {
                        const diameterIndex = unifiedBranchInfo[0].index;

                        // Only show this diameter ring if its index matches a length endpoint.
                        if (lengthEndpointIndices.has(diameterIndex)) {
                            const branchInfo = unifiedBranchInfo[0];
                            const branchName = branchInfo?.branch?.name;
                            const branchType = getBranchTypeFromName(branchName);

                            // Convert to master index and show the ring.
                            const masterIndex = convertUnifiedBranchIndexToMasterIndex(
                                diameterIndex,
                                branchType,
                                meshesRef.current.wallCenterline.dataSource.getFieldData()
                            );
                            const ring = meshesRef.current.diameterRings.get(masterIndex);

                            if (ring) {
                                parentContextRef.current.renderer.addActor(ring.actor);
                                ring.actor.getProperty().setColor(0, 0, 1);
                            }
                        }
                    }
                });

                parentContextRef.current.renderer.getRenderWindow().render();
            }

            layer.draw();
            log("Length screenshot view prepared");

            return captureCompositeScreenshot();
        }
        //TODO: Move camera functions copied from MeshVisualization to new shared file
        function calculateAzimuth(dop, viewUp) {
            if (Math.abs(dop[0]) < 0.001 && Math.abs(dop[1]) < 0.001) {
                return vtkMath.degreesFromRadians(Math.atan2(viewUp[0], viewUp[1]));
            } else {
                return vtkMath.degreesFromRadians(Math.atan2(dop[0], dop[1]));
            }
        }
        function calculateElevation(dop, viewUp) {
            const elevation = -vtkMath.degreesFromRadians(Math.atan2(dop[2], Math.hypot(dop[0], dop[1])));
            const isUpsideDown = vtkMath.dot(viewUp, [0, 0, 1]) <= 0; // Check if we are upside-down
            if (!isUpsideDown) {
                return elevation;
            } else {
                if (elevation > 0) { return 180 - elevation; }
                else { return (-180) - elevation; }
            }
        }
        function multiplyScalar3D(v, scalar) {
            return [v[0] * scalar, v[1] * scalar, v[2] * scalar];
        }

        function rotate(position, rotationMatrix) {
            const newPos = [0, 0, 0];
            vtkMath.multiplyMatrix(rotationMatrix, position, 3, 3, 3, 1, newPos);
            return newPos;
        }

        function rotateX(position, angle) {
            const rotationMatrix =
                [
                    1, 0, 0,
                    0, Math.cos(angle), -Math.sin(angle),
                    0, Math.sin(angle), Math.cos(angle)
                ];
            return rotate(position, rotationMatrix);
        }

        function rotateY(position, angle) {
            const rotationMatrix =
                [
                    Math.cos(angle), 0, Math.sin(angle),
                    0, 1, 0,
                    -Math.sin(angle), 0, Math.cos(angle)
                ];
            return rotate(position, rotationMatrix);
        }

        function rotateZ(position, angle) {
            const rotationMatrix =
                [
                    Math.cos(angle), -Math.sin(angle), 0,
                    Math.sin(angle), Math.cos(angle), 0,
                    0, 0, 1
                ];
            return rotate(position, rotationMatrix);
        }

        function rotateAzimuthAroundUp(v, angleDelta) {
            const angle = vtkMath.radiansFromDegrees(angleDelta);
            return rodriguesRotation(v, [0, 0, 1], angle);
        }

        function rotateElevation(pos, angleDelta) {
            const angle = vtkMath.radiansFromDegrees(angleDelta);
            return rotateX(pos, angle);
        }

        function rodriguesRotation(v, k, angle) {
            const cos = Math.cos(angle);
            const sin = Math.sin(angle);
            const vcos = multiplyScalar3D(v, cos);
            const kv = vtkMath.cross(k, v, [0, 0, 0]);
            const kvsin = multiplyScalar3D(kv, sin);
            const kdotv = vtkMath.dot(k, v);
            const kkvcos = multiplyScalar3D(k, (1 - cos) * kdotv);
            return vtkMath.add(vtkMath.add(vcos, kvsin, [0, 0, 0]), kkvcos, [0, 0, 0]);
        }
        function rotateElevationAroundAxis(v, dop, viewUp, angleDelta) {
            const angle = vtkMath.radiansFromDegrees(angleDelta);
            const k = [0, 0, 0];
            if (!vtkMath.areEquals([dop[0], dop[1], Math.abs(dop[2])], [0, 0, 1], 0.0001)) {
                vtkMath.cross(dop, [0, 0, 1], k);
            } else {
                vtkMath.cross(dop, viewUp, k);
            }
            vtkMath.normalize(k);
            return rodriguesRotation(v, k, angle);
        }
        function rotateCamera(camera, angles) {
            const dop = camera.getDirectionOfProjection();
            const pos = camera.getPosition();
            const fp = camera.getFocalPoint();
            const relativePosition = vtkMath.subtract(pos, fp, [0, 0, 0]);
            const initialViewUp = camera.getViewUp();

            const azimuthDelta = calculateAzimuth(dop, initialViewUp) - angles.azimuth;
            const azimuthRotatedPosition = rotateAzimuthAroundUp(relativePosition, azimuthDelta);
            const azimuthRotatedViewUp = rotateAzimuthAroundUp(initialViewUp, azimuthDelta);

            const elevationDelta = calculateElevation(dop, initialViewUp) - angles.elevation;
            const elevationRotatedPosition = rotateElevation(azimuthRotatedPosition, elevationDelta);
            const elevationRotatedViewUp = rotateElevation(azimuthRotatedViewUp, elevationDelta);

            const finalPosition = vtkMath.add(elevationRotatedPosition, fp, [0, 0, 0]);

            camera.setViewUp(elevationRotatedViewUp);
            camera.setPosition(finalPosition[0], finalPosition[1], finalPosition[2]);
        }

        function resetCamera() {
            if (context.current && parentContextRef.current?.cameraProps.initialCameraAdjustment) {
                const adjustment = Vector3.ofVtk(parentContextRef.current.cameraProps.initialCameraAdjustment);
                setRendererViewPlaneNormal(parentContextRef.current.renderer, adjustment);
            }
        }

        function roundToPrecision(num, precision) {
            const multiplier = Math.pow(10, precision);
            return (Math.round(num * multiplier)) / multiplier;
        }

        function incrementAngle(azimuth, elevation) {
            if (parentContextRef.current) {
                const { renderWindow, renderer } = parentContextRef.current;
                const camera = renderer.getActiveCamera();
                const pos = camera.getPosition();
                const fp = camera.getFocalPoint();
                const relativePosition = vtkMath.subtract(pos, fp, [0, 0, 0]);
                const initialViewUp = camera.getViewUp();
                const dop = camera.getDirectionOfProjection();

                const azimuthRotatedPosition = rotateAzimuthAroundUp(relativePosition, azimuth);
                const azimuthRotatedDop = rotateAzimuthAroundUp(dop, azimuth);
                const azimuthRotatedViewUp = rotateAzimuthAroundUp(initialViewUp, azimuth);

                // Rounding errors can cause weird behaviours at certain angles, so we use fixed precision here
                const approxViewUp = [
                    roundToPrecision(initialViewUp[0], 4),
                    roundToPrecision(initialViewUp[1], 4),
                    roundToPrecision(initialViewUp[2], 4)
                ];
                const sign = vtkMath.dot(approxViewUp, [0, 0, 1]) >= 0 ? 1 : -1;
                const adjustedElevation = elevation * sign;

                const elevationRotatedPosition = rotateElevationAroundAxis(azimuthRotatedPosition, azimuthRotatedDop, azimuthRotatedViewUp, adjustedElevation);
                const elevationRotatedViewUp = rotateElevationAroundAxis(azimuthRotatedViewUp, azimuthRotatedDop, azimuthRotatedViewUp, adjustedElevation);

                const finalPosition = vtkMath.add(elevationRotatedPosition, fp, [0, 0, 0]);

                camera.setViewUp(elevationRotatedViewUp);
                camera.setPosition(finalPosition[0], finalPosition[1], finalPosition[2]);
                renderer.resetCameraClippingRange();
                renderWindow.render();
            }
        }

        function handleZoomedInScreenshot(index, branch, angles, showGroups, zoomFactor, pitch, compositeScreenshotType) {
            if (!context.current?.layer || !context.current?.konvaElements || !parentContextRef.current?.renderer || !parentContextRef.current?.renderWindow) {
                console.error("Cannot handle zoomed in screenshot: context not initialized");
                return;
            }

            try {
                const branchType = branch.name === 'Left' ? CenterlineBranchSelection.TRANSITION_LEFT : CenterlineBranchSelection.TRANSITION_RIGHT;
                const masterIndex = convertUnifiedBranchIndexToMasterIndex(index, branchType, meshes.wallCenterline.dataSource.getFieldData());
                const pointData = meshes.wallCenterline.dataSource.getPoints();
                const [x, y, z] = pointData.getTuple(masterIndex);
                const camera = parentContextRef.current.renderer.getActiveCamera();
                camera.setFocalPoint(x, y, z);

                vtkInteractorStyleManipulator.dollyByFactor(
                    parentContextRef.current.renderWindow.getInteractor(),
                    parentContextRef.current.renderer,
                    zoomFactor,
                );
                camera.pitch(pitch); //This says pitch but it pans vertically I swear
                const layer = context.current.layer;
                const konvaElements = context.current.konvaElements;

                incrementAngle(angles.azimuth, 0);
                incrementAngle(0, angles.elevation);

                toggleLabelsByGroup(layer, konvaElements, showGroups);
                expandParentGroups(layer, konvaElements, LabelType.DIAMETER);
                updateLabelLayer(context, context.current.stentPlanningProps, meshes, parentContext, 1.6);
                //This is handled by StentPlanningCenterlines so that the centerline updates and scales
                dispatchEvent(new Event("screenshotUpdate"));

                // Hide all diameter rings first for initial camera setup.
                if (meshesRef.current && parentContextRef.current) {
                    meshesRef.current.diameterRings.forEach((diameterRing) => {
                        parentContextRef.current.renderer.removeActor(diameterRing.actor);
                    });
                }

                const lengthNames = [
                    measurementNames.NeckLength,
                    measurementNames.BoaLength,
                    measurementNames.Bifurc,
                    measurementNames.Left,
                    measurementNames.Right,
                    measurementNames.RCIALength,
                    measurementNames.LCIALength
                ];

                const activeLengths = showGroups?.filter(groupName => lengthNames.includes(groupName));
                const activeDiameters = [];
                activeLengths.forEach(lengthName => {
                    activeDiameters.push(measurementLabelInfo[lengthName]?.firstPlaneChildren[0]);
                    activeDiameters.push(measurementLabelInfo[lengthName]?.secondPlaneChildren[0]);
                });

                // Collect the ring indices we need for this screenshot.
                const neededRingIndices = [];
                const collectRingIndices = (measurement) => {
                    const measurementName = measurement.fields[0].MeasurementName;
                    const labelInfo = measurementLabelInfo[measurementName];
                    const groupName = labelInfo?.parentGroup || labelInfo?.childGroup || measurementName || "";
                    if (!showGroups.includes(groupName)) {
                        return;
                    }
                    const unifiedBranchInfo = measurement.fields[1];
                    const branchInfo = unifiedBranchInfo[0];
                    const branchName = branchInfo?.branch?.name;
                    const branchType = getBranchTypeFromName(branchName);

                    if (getDUName(measurement).includes('Diameter') && !isNullOrUndefined(unifiedBranchInfo[0]?.index)) {
                        const masterIdx = convertUnifiedBranchIndexToMasterIndex(
                            unifiedBranchInfo[0].index,
                            branchType,
                            meshesRef.current.wallCenterline.dataSource.getFieldData()
                        );
                        if (!neededRingIndices.includes(masterIdx)) {
                            neededRingIndices.push(masterIdx);
                        }
                    } else if (getDUName(measurement).includes('Length')) {
                        if (!isNullOrUndefined(unifiedBranchInfo[0]?.firstIndex)) {
                            const firstMasterIdx = convertUnifiedBranchIndexToMasterIndex(
                                unifiedBranchInfo[0].firstIndex,
                                branchType,
                                meshesRef.current.wallCenterline.dataSource.getFieldData()
                            );
                            if (!neededRingIndices.includes(firstMasterIdx)) {
                                neededRingIndices.push(firstMasterIdx);
                            }
                        }
                        if (!isNullOrUndefined(unifiedBranchInfo[0]?.secondIndex)) {
                            const secondMasterIdx = convertUnifiedBranchIndexToMasterIndex(
                                unifiedBranchInfo[0].secondIndex,
                                branchType,
                                meshesRef.current.wallCenterline.dataSource.getFieldData()
                            );
                            if (!neededRingIndices.includes(secondMasterIdx)) {
                                neededRingIndices.push(secondMasterIdx);
                            }
                        }
                    }
                };

                if (stentPlanningPropsRef.current?.currentMeasurements) {
                    stentPlanningPropsRef.current.currentMeasurements.forEach(collectRingIndices);
                }

                // Update diameter rings for all visible diameter measurements.
                const activeRings = [];
                updateRingsForMeasurements(layer, konvaElements, activeRings, (measurement) => {
                    const measurementName = measurement.fields[0].MeasurementName;
                    const labelInfo = measurementLabelInfo[measurementName];
                    const groupName = labelInfo?.parentGroup || labelInfo?.childGroup || measurementName || "";
                    const res = showGroups.includes(groupName);
                    return res;
                }, activeDiameters);

                parentContextRef.current.renderWindow.render();
                context.current.stage.draw();

                const screenshotType = getDUName(compositeScreenshotType);

                adjustCameraForScreenshotType(
                    context.current,
                    parentContextRef.current,
                    meshesRef.current,
                    showGroups,
                    screenshotType
                );

                // Update centerline to match new camera position.
                dispatchEvent(new Event("screenshotUpdate"));

                // IMPORTANT: After camera adjustment and screenshotUpdate event, external code may have
                // re-added all rings based on Elmish state. We need to remove all rings and re-add
                // only the ones we need for this screenshot.
                if (meshesRef.current && parentContextRef.current) {
                    // Remove all rings.
                    meshesRef.current.diameterRings.forEach((diameterRing) => {
                        parentContextRef.current.renderer.removeActor(diameterRing.actor);
                    });

                    // Add back only the needed rings.
                    neededRingIndices.forEach((ringIndex) => {
                        const ring = meshesRef.current.diameterRings.get(ringIndex);
                        if (ring) {
                            parentContextRef.current.renderer.addActor(ring.actor);
                            ring.actor.getProperty().setColor(0, 0, 1);
                        }
                    });
                }

                // Re-update label positions after camera adjustment, but skip adding rings.
                updateLabelLayer(context, context.current.stentPlanningProps, meshes, parentContext, 1.6, true);
                parentContextRef.current.renderWindow.render();
                context.current.stage.draw();

                const vtkCanvas = parentContextRef.current.genericRenderWindow
                    .getApiSpecificRenderWindow()
                    .getCanvas();

                if (!vtkCanvas) {
                    console.error("VTK canvas not available");
                    return;
                }

                const width = vtkCanvas.width;
                const height = vtkCanvas.height;
                const dpr = window.devicePixelRatio || 1;

                const compositeCanvas = document.createElement('canvas');
                compositeCanvas.width = width / dpr;
                compositeCanvas.height = height / dpr;
                const compositeContext = compositeCanvas.getContext('2d');

                if (!compositeContext) {
                    console.error("Failed to create composite canvas context");
                    return;
                }

                // Draw VTK canvas (3D mesh) as the base layer.
                compositeContext.drawImage(vtkCanvas, 0, 0, compositeCanvas.width, compositeCanvas.height);

                // Get the Konva label layer canvas.
                const labelLayerCanvas = context.current.layer?.getCanvas?.();

                // Draw Konva centerline canvas if available.
                const allCanvases = context.current.stentContainer?.querySelectorAll('canvas');
                if (allCanvases) {
                    allCanvases.forEach(canvas => {
                        // Skip the label layer canvas (will be drawn last)
                        if (canvas !== labelLayerCanvas?._canvas && canvas !== vtkCanvas) {
                            compositeContext.drawImage(canvas, 0, 0, compositeCanvas.width, compositeCanvas.height);
                        }
                    });
                }

                // Draw Konva labels canvas (should be on top).
                if (labelLayerCanvas) {
                    const canvasElement = labelLayerCanvas._canvas || labelLayerCanvas;
                    if (canvasElement) {
                        compositeContext.drawImage(canvasElement, 0, 0, compositeCanvas.width, compositeCanvas.height);
                    }
                }

                const b64Data = compositeCanvas.toDataURL('image/png');
                debug(() => window.open(b64Data, '_blank'));
                return b64Data.split(',')[1];

            } catch (error) {
                console.error("Error capturing screenshot:", error);
            }
        }

        function captureCompositeScreenshot() {
            if (!parentContextRef.current?.genericRenderWindow || !context.current?.stage) {
                console.error("Cannot capture screenshot: missing required contexts");
                return;
            }

            try {
                const vtkCanvas = parentContextRef.current.genericRenderWindow
                    .getApiSpecificRenderWindow()
                    .getCanvas();

                if (!vtkCanvas) {
                    console.error("VTK canvas not available");
                    return;
                }

                const width = vtkCanvas.width;
                const height = vtkCanvas.height;
                const dpr = window.devicePixelRatio || 1;

                const compositeCanvas = document.createElement('canvas');
                compositeCanvas.width = width / dpr;
                compositeCanvas.height = height / dpr;
                const compositeContext = compositeCanvas.getContext('2d');

                if (!compositeContext) {
                    console.error("Failed to create composite canvas context");
                    return;
                }

                // Draw VTK canvas (3D mesh) as the base layer.
                compositeContext.drawImage(vtkCanvas, 0, 0, compositeCanvas.width, compositeCanvas.height);

                // Get the Konva label layer canvas.
                const labelLayerCanvas = context.current.layer?.getCanvas?.();

                // Draw Konva centerline canvas if available.
                const allCanvases = context.current.stentContainer?.querySelectorAll('canvas');
                if (allCanvases) {
                    allCanvases.forEach(canvas => {
                        // Skip the label layer canvas (will be drawn last)
                        if (canvas !== labelLayerCanvas?._canvas && canvas !== vtkCanvas) {
                            compositeContext.drawImage(canvas, 0, 0, compositeCanvas.width, compositeCanvas.height);
                        }
                    });
                }

                // Draw Konva labels canvas (should be on top).
                if (labelLayerCanvas) {
                    const canvasElement = labelLayerCanvas._canvas || labelLayerCanvas;
                    if (canvasElement) {
                        compositeContext.drawImage(canvasElement, 0, 0, compositeCanvas.width, compositeCanvas.height);
                    }
                }

                const b64Data = compositeCanvas.toDataURL('image/png').split(',')[1];
                return b64Data;
            } catch (error) {
                console.error("Error capturing screenshot:", error);
            }
        }

        function captureScreenshots(ev) {
            if (typeof ev.detail !== 'function') {
                console.error("Invalid event detail for screenshot capture");
                return;
            }

            const { genericRenderWindow, renderWindow } = parentContextRef.current;
            if (!(genericRenderWindow && renderWindow)) { return; }

            const waitForCanvasReady = () => {

                const vtkCanvas = genericRenderWindow.getApiSpecificRenderWindow().getCanvas();
                if (!vtkCanvas) {
                    console.error("VTK canvas not available for screenshot");
                    callback("");
                    return;
                }

                const canvasReady = vtkCanvas.width > 0 && vtkCanvas.height > 0;

                if (!canvasReady) {
                    renderWindow.render();
                    requestAnimationFrame(waitForCanvasReady);
                    return;
                }

                requestAnimationFrame(() => {
                    renderWindow.render();
                    capture();
                });

                return;
            };

            const capture = () => {
                const originalState = captureOriginalState();

                // Wait for a short delay to ensure all rendering is complete.
                setTimeout(() => {
                    const diameterScreenshot = handleDiameterScreenshot();
                    const lengthScreenshot = handleLengthScreenshot();

                    restoreOriginalState(originalState);

                    // Return both screenshots via the event detail callback.
                    ev.detail({
                        Diameters: diameterScreenshot,
                        LengthsAndCArms: lengthScreenshot
                    });
                }, 100);
            };

            requestAnimationFrame(waitForCanvasReady);
        }

        function captureOriginalState() {
            if (!context.current?.layer || !context.current?.konvaElements) {
                return null;
            }

            const layer = context.current.layer;
            const konvaElements = context.current.konvaElements;
            const savedState = {
                labelVisibility: {},
                groupVisibility: {},
                visibilityLabelText: {},
                disableLabelText: {},
                visibleRings: new Set()
            };

            // Save visibility state for all labels and lines.
            for (const measurementName in measurementLabelInfo) {
                const elements = konvaElements[measurementName];
                if (elements) {
                    savedState.labelVisibility[measurementName] = {
                        stentLabel: elements.stentLabel?.visible() ?? false,
                        valueLabel: elements.valueLabel?.visible() ?? false,
                        labelLine: elements.labelLine?.visible() ?? false,
                        visibilityLabel: elements.visibilityLabel?.visible() ?? false,
                        disableLabel: elements.disableLabel?.visible() ?? false
                    };

                    // Save visibility label text (▼ or ►).
                    if (elements.visibilityLabel) {
                        const visibilityText = elements.visibilityLabel.getText();
                        if (visibilityText) {
                            savedState.visibilityLabelText[measurementName] = visibilityText.text();
                        }
                    }

                    if (elements.disableLabel) {
                        const disableText = elements.disableLabel.getText();
                        if (disableText) {
                            savedState.disableLabelText[measurementName] = disableText.text();
                        }
                    }
                }

                // Save group visibility state.
                const labelInfo = measurementLabelInfo[measurementName];
                if (labelInfo?.parentGroup) {
                    const group = layer.findOne(`#${labelInfo.parentGroup}`);
                    if (group) {
                        savedState.groupVisibility[labelInfo.parentGroup] = {
                            visible: group.visible(),
                            clicked: group.getAttr('clicked') ?? false
                        };
                    }
                }
            }

            // Save currently visible diameter rings.
            if (meshesRef.current && parentContextRef.current) {
                meshesRef.current.diameterRings.forEach((diameterRing, index) => {
                    // Check if the ring actor is currently in the renderer.
                    const actors = parentContextRef.current.renderer.getActors();
                    if (actors.includes(diameterRing.actor)) {
                        savedState.visibleRings.add(index);
                    }
                });
            }

            return savedState;
        }

        function restoreOriginalState(savedState) {
            if (!savedState || !context.current?.layer || !context.current?.konvaElements) {
                console.error("Cannot restore state: missing saved state or context");
                return;
            }

            const layer = context.current.layer;
            const konvaElements = context.current.konvaElements;

            // Restore label and line visibility.
            for (const measurementName in savedState.labelVisibility) {
                const elements = konvaElements[measurementName];
                const visibility = savedState.labelVisibility[measurementName];

                if (elements && visibility) {
                    // Restore visibility for all element types.
                    ['stentLabel', 'valueLabel', 'labelLine', 'visibilityLabel', 'disableLabel'].forEach(key => {
                        elements[key]?.visible(visibility[key]);
                    });
                }

                // Restore visibility label text.
                if (savedState.visibilityLabelText[measurementName] && elements?.visibilityLabel) {
                    const visibilityText = elements.visibilityLabel.getText();
                    if (visibilityText) {
                        visibilityText.text(savedState.visibilityLabelText[measurementName]);
                    }
                }

                if (savedState.disableLabelText[measurementName] && elements?.disableLabel) {
                    const disableText = elements.disableLabel.getText();
                    if (disableText) {
                        disableText.text(savedState.disableLabelText[measurementName]);
                    }
                }
            }

            // Restore group visibility state.
            for (const groupName in savedState.groupVisibility) {
                const group = layer.findOne(`#${groupName}`);
                const groupState = savedState.groupVisibility[groupName];

                if (group && groupState) {
                    group.setAttr('clicked', groupState.clicked);
                    group.visible(groupState.visible);
                }
            }

            // Restore diameter rings.
            if (meshesRef.current && parentContextRef.current) {
                // First, remove all diameter rings.
                meshesRef.current.diameterRings.forEach((diameterRing) => {
                    parentContextRef.current.renderer.removeActor(diameterRing.actor);
                });

                // Then, restore only the rings that were originally visible.
                savedState.visibleRings.forEach((index) => {
                    const ring = meshesRef.current.diameterRings.get(index);
                    if (ring) {
                        parentContextRef.current.renderer.addActor(ring.actor);
                    }
                });

                parentContextRef.current.renderer.getRenderWindow().render();
            }

            layer.draw();
            log("Original state restored after screenshot capture");
        }

        function captureZoomedInScreenshot(ev) {
            if (typeof ev.detail?.callback !== 'function') {
                console.error("Invalid event detail for zoomed-in screenshot capture");
                return;
            }

            const { genericRenderWindow, renderWindow } = parentContextRef.current;
            if (!(genericRenderWindow && renderWindow)) { return; }

            const waitForCanvasReady = () => {

                const vtkCanvas = genericRenderWindow.getApiSpecificRenderWindow().getCanvas();
                if (!vtkCanvas) {
                    console.error("VTK canvas not available for screenshot");
                    callback("");
                    return;
                }

                const canvasReady = vtkCanvas.width > 0 && vtkCanvas.height > 0;

                if (!canvasReady) {
                    renderWindow.render();
                    requestAnimationFrame(waitForCanvasReady);
                    return;
                }

                requestAnimationFrame(() => {
                    renderWindow.render();
                    capture();
                });
            };

            const capture = () => {
                const originalState = captureOriginalState();
                const zoomedInScreenshot =
                    handleZoomedInScreenshot(
                        ev.detail.focalPointIndex,
                        ev.detail.focalPointBranch,
                        ev.detail.angles,
                        ev.detail.visibleLabelGroupNames,
                        ev.detail.zoomFactor,
                        ev.detail.pitch,
                        ev.detail.screenshotType
                    );

                // Wait for a short delay to ensure all rendering is complete.
                setTimeout(() => {
                    const camera = parentContextRef.current.renderer.getActiveCamera();
                    resetCamera();
                    parentContextRef.current.renderer.resetCamera();
                    parentContextRef.current.renderer.resetCameraClippingRange();
                    parentContextRef.current.renderWindow.render();
                    const dop = camera.getDirectionOfProjection();
                    const viewUp = camera.getViewUp();
                    rotateCamera(
                        parentContextRef.current.renderer.getActiveCamera(),
                        { azimuth: calculateAzimuth(dop, viewUp), elevation: calculateElevation(dop, viewUp) });
                    context.current.stage.draw();
                    // Return screenshots via the event detail callback.
                    ev.detail.callback(zoomedInScreenshot);
                }, 100);
                restoreOriginalState(originalState);
            };

            requestAnimationFrame(waitForCanvasReady);
        }

        window.addEventListener(stentPlanningProps.eventNames.CaptureStentPlanScreenshots, captureScreenshots);
        window.addEventListener(stentPlanningProps.eventNames.CaptureStentPlanZoomedInScreenshot, captureZoomedInScreenshot);
        return [
            { eventType: stentPlanningProps.eventNames.CaptureStentPlanScreenshots, handler: captureScreenshots, container: window },
            { eventType: stentPlanningProps.eventNames.CaptureStentPlanZoomedInScreenshot, handler: captureZoomedInScreenshot, container: window },
        ];
    }

    // Effect to handle mounting and unmounting.
    useEffect(() => {
        log("Mounting");

        return () => {
            log("Unmounting");

            // Just using a more convenient name; no magic here.
            const ctx = context.current;

            if (ctx) {
                log("Cleaning up");

                if (ctx.eventHandlers) {
                    // Iterate through the list and remove each event listener.
                    ctx.eventHandlers.forEach(({ eventType, handler, container }) => {
                        if (container && handler) {
                            log('Removing event listener for', eventType, 'from', container);
                            container.removeEventListener(eventType, handler);
                        }
                    });
                }

                if (ctx.stage) {
                    ctx.stage.destroy();
                }

                if (ctx.stentContainer && container) {
                    container.removeChild(ctx.stentContainer);
                }

                if (ctx.resizeObserver) {
                    ctx.resizeObserver.disconnect();
                }
                ctx.stage = null;
                ctx.layer = null;
                ctx.konvaElements = null;
                ctx.stentContainer = null;
                ctx.resizeObserver = null;
                ctx.stentPlanningProps = null;
            }
        };
    }, []);

    useEffect(() => {
        if (container && !context.current && parentContext) {
            log("Initializing");

            const stentContainer = document.createElement('div');
            stentContainer.id = 'stentContainer';
            stentContainer.style.position = 'absolute';
            stentContainer.style.top = '0';
            stentContainer.style.left = '0';
            stentContainer.style.width = '100%';
            stentContainer.style.height = '100%';
            stentContainer.style.zIndex = '1';
            container.appendChild(stentContainer);

            context.current = {
                stentContainer,
                stage: null,
                layer: null,
                resizeObserver: null,
                konvaElements: {}
            };
        }
    }, [container, parentContext]);

    // Effect to set up resize observer.
    useEffect(() => {
        if (container && context.current && parentContext && stentPlanningProps && meshes) {
            context.current.stentPlanningProps = stentPlanningProps;
            const handleResize = () => {
                log("Resizing");
                if (context.current?.stentContainer && context.current?.stage && context.current?.layer) {
                    const props = context.current.stentPlanningProps;
                    context.current.stage.width(container.clientWidth);
                    context.current.stage.height(container.clientHeight);
                    updateLabelLayer(context, props, meshes, parentContext);
                    context.current.layer.draw();
                }
            };
            if (isNullOrUndefined(context.current.resizeObserver)) {
                const resizeObserver = new ResizeObserver(handleResize);
                resizeObserver.observe(container);
                context.current.resizeObserver = resizeObserver;
            }
        }
    }, [container, parentContext, stentPlanningProps, meshes]);

    // Effect to create/update the Konva stage and labels.
    useEffect(() => {
        if (container && parentContext && context.current?.stentContainer && stentPlanningProps && meshes) {
            // Hide the previously shown diameter rings
            meshes.diameterRings.forEach((diameterRing) => {
                parentContext.renderer.removeActor(diameterRing.actor);
            });

            context.current.stentPlanningProps = stentPlanningProps;

            if (!context.current.stage && stentPlanningProps) {
                log("Creating stage");
                const stentContainer = context.current.stentContainer;

                context.current.stage = new Konva.Stage({
                    container: stentContainer.id,
                    width: stentContainer.clientWidth,
                    height: stentContainer.clientHeight
                });

                const layer = new Konva.Layer({ name: 'labelLayer' });
                context.current.layer = layer;
                context.current.stage.add(layer);
                // Init the layer

                const activeRings = [];
                stentPlanningProps.currentMeasurements.forEach((measurement) => {
                    log("Processing measurement: " + measurement.fields[0].MeasurementName);
                    debug(() => global.debugCurrentMeasurements = stentPlanningProps.currentMeasurements);
                    debug(() => global.debugParentContext = parentContextRef);
                    // Unpacking the StentMeasurement DU
                    // The DU is made up of 2 components:
                    //   fields[0] - MeasurementInfo {MeasurementName, RelatedMeasurementNames, MeasurementBounds}
                    //   fields[1] - The unified branch measurement
                    const measurementName = measurement.fields[0].MeasurementName;
                    const measurementBounds = measurement.fields[0].MeasurementBounds;
                    const labelInfo = measurementLabelInfo[measurementName];
                    const unifiedBranchInfo = measurement.fields[1];
                    const isActive = stentPlanningProps.maybeActiveMeasurement?.fields[0].MeasurementName === measurementName;

                    const isIndexValid =
                        measurement.name.includes('Diameter')
                            ? indexInBounds(unifiedBranchInfo[0].index, measurementBounds, meshes.wallCenterline.dataSource.getFieldData())
                            : true;

                    if (measurement.name === 'CArmAngle') {
                        log("Adding label: " + labelInfo.labelName);
                        const azimuthAngle = unifiedBranchInfo.FirstAngle;
                        const elevationAngle = unifiedBranchInfo.SecondAngle;

                        const displayAzimuth = getAzimuthDisplayAngle(azimuthAngle);
                        const displayElevation = getElevationDisplayAngle(elevationAngle);

                        const valueText = `${displayAzimuth.angle.toFixed(0)}° ${displayAzimuth.label}, ${displayElevation.angle.toFixed(0)}° ${displayElevation.label}`;

                        const { visibilityLabel, stentLabel, valueLabel, disableLabel, group } =
                            addLabelAndChildren(
                                parentContext,
                                context,
                                stentPlanningProps,
                                measurementName,
                                layer,
                                labelInfo,
                                valueText,
                                true,
                                isIndexValid
                            );

                        parentContext.renderer.getRenderWindow().render();

                        context.current.konvaElements[measurementName] = {
                            stentLabel: stentLabel,
                            valueLabel: valueLabel,
                            labelLine: null,
                            visibilityLabel: visibilityLabel,
                            disableLabel: disableLabel
                        };

                        return;
                    }

                    const measurements = unifiedBranchInfo[1];
                    const branchInfo = unifiedBranchInfo[0];
                    const branchName = branchInfo.branch.name;
                    let branchType = null;
                    let valueText = "";

                    if (branchName === CenterlineDistanceBranchSelection.LEFT) {
                        branchType = CenterlineBranchSelection.DISTANCE_LEFT;
                    } else if (branchName === CenterlineDistanceBranchSelection.CUSP) {
                        branchType = CenterlineBranchSelection.DISTANCE_CUSP;
                    } else if (branchName === CenterlineDistanceBranchSelection.RIGHT) {
                        branchType = CenterlineBranchSelection.DISTANCE_RIGHT;
                    }

                    if (measurement.name.includes('Diameter')) {
                        const displayMeasurement = getDisplayMeasurement(measurements, labelInfo?.displayMeasurementType);
                        valueText = isIndexValid ? displayMeasurement.toFixed(1) : "--";
                    } else if (measurement.name === 'Length') {
                        valueText = isIndexValid ? measurements.toFixed(1) : "--";
                    }

                    log("Adding label: " + labelInfo.labelName);
                    const labelElements = addLabelAndChildren(parentContext, context, stentPlanningProps, measurementName, layer, labelInfo, valueText, true, isIndexValid);
                    const { visibilityLabel, stentLabel, valueLabel, disableLabel } = labelElements;
                    let { group } = labelElements;

                    let line = null;

                    const filter =
                        makeRootFilter(
                            CenterlineType.WALL,
                            branchType,
                            meshes,
                            stentPlanningProps.fieldDataArrayNames
                        );

                    if (isIndexValid) {
                        const screenCoords = getCenterlineCoords(filter, unifiedBranchInfo, parentContext.renderer);
                        const [targetLabel, isRightSideLabel] = (labelInfo.xOffset >= rightSideXOffset) ? [stentLabel, true] : [disableLabel || valueLabel, false];
                        line = mkLine(measurementName, targetLabel, screenCoords, isRightSideLabel, labelInfo.labelColor, (1 / window.devicePixelRatio));
                        line.strokeWidth(Math.max(2, 2 * (1 / window.devicePixelRatio)));
                        log(line.points());
                        if (!isNullOrUndefined(labelInfo.childGroup) && labelInfo.childGroup.trim().length !== 0) {
                            group = layer.findOne(`#${labelInfo.childGroup}`);
                            if (!isNullOrUndefined(group)) {
                                group.add(line);
                            }
                        } else {
                            layer.add(line);
                        }
                    }

                    const updateRingsArgs = {
                        group,
                        measurementName: measurement.name,
                        unifiedBranchInfo,
                        branchType,
                        meshes,
                        labelInfo,
                        parentContext,
                        line,
                        isActive,
                        isDisabled: disableLabel?.getAttr('clicked'),
                        activeRings
                    };

                    updateRings(updateRingsArgs);
                    parentContext.renderer.getRenderWindow().render();
                    context.current.konvaElements[measurementName] = { stentLabel: stentLabel, valueLabel: valueLabel, labelLine: line, visibilityLabel: visibilityLabel, disableLabel: disableLabel };
                });

                const screenshotHandlers = setUpScreenshotHandler();
                context.current = { ...context.current, eventHandlers: screenshotHandlers };
                layer.draw();
            } else {
                updateLabelLayer(context, stentPlanningProps, meshes, parentContext);
            }
            context.current.layer.draw();
        }
    }, [container, parentContext, meshes, stentPlanningProps]);

    return context.current?.stage;
}

// TODO: VRM-4458 Uncomment if we need to capture axial screenshots.
// /**
//  * Get the world coordinates for a given centerline master index.
//  * @param {number} masterIndex - The master centerline index.
//  * @param {Object} meshes - The meshes object containing centerline data.
//  * @returns {number[]|null} The world coordinates [x, y, z] or null if unavailable.
//  */
// export function getCenterlineWorldPosition(masterIndex, meshes) {
//     if (!meshes || !meshes.wallCenterline || !meshes.wallCenterline.dataSource) {
//         console.error("Meshes or wall centerline not available");
//         return null;
//     }
//
//     const points = meshes.wallCenterline.dataSource.getPoints();
//     if (!points || masterIndex < 0 || masterIndex >= points.getNumberOfPoints()) {
//         console.error(`Invalid master index ${masterIndex}`);
//         return null;
//     }
//
//     const position = points.getTuple(masterIndex);
//     return position;
// }